import {
  CircleDot,
  Diamond,
  Columns3,
  Crosshair,
  DoorOpen,
  FileUp,
  Link2Off,
  Maximize2,
  Ruler,
  SquareX,
  Tags,
  Trash2,
  Undo2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import type { ApartmentAverageForProject } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import {
  polygonArea,
  polygonEdges,
  polygonPerimeter,
  segmentLength,
  type Point,
} from '@obraplan/core/domain/takeoff/polygon';
import { ROOM_KIND_LABEL, type Room, type RoomKind } from '@obraplan/core/domain/takeoff/room';
import { edgeWithoutWallAt } from '@obraplan/core/domain/takeoff/room-edge';
import { edgesWithoutWallOf } from '@obraplan/core/domain/takeoff/geometry';
import {
  FLOOR_PLAN_KIND_LABEL,
  FLOOR_PLAN_KIND_ORDER,
  type FloorPlanKind,
} from '@obraplan/core/domain/takeoff/floor-plan';
import {
  OPENING_KIND_LABEL,
  wallSideOf,
  type OpeningKind,
  type Wall,
} from '@obraplan/core/domain/takeoff/wall';
import {
  checkElementOutline,
  orientationLabel,
  STRUCTURAL_ELEMENT_KIND_LABEL,
  type StructuralElement,
  type StructuralElementKind,
} from '@obraplan/core/domain/takeoff/structural-element';
import type { StructuralElementType } from '@obraplan/core/domain/takeoff/element-type';
import {
  roomOfShaft,
  shaftArea,
  shaftContour,
  shaftLength,
  shaftWidth,
  SHAFT_LABEL,
  type Shaft,
} from '@obraplan/core/domain/takeoff/shaft';
import type {
  OpeningId,
  ProjectId,
  RoomId,
  ShaftId,
  StructuralElementId,
  StructuralPieceId,
  WallId,
} from '@obraplan/core/domain/shared/ids';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import {
  closesPolygon,
  distance,
  edgeAt,
  isSquare,
  metresPerPixel,
  metresToPixel,
  pixelToMetres,
  placeElements,
  placeOpenings,
  placeWalls,
  resolvePoint,
  serratedPath,
  slideDistance,
  slideEdge,
  traceStructure,
  unplacedWalls,
  wallAt,
  type EdgeRef,
  type Pixel,
  type PlacedElement,
  type PlacedOpening,
  type PlacedWall,
  type SlidOutline,
  type TracedStructure,
} from './plan-tracer';
import {
  DEFAULT_WALL_HEIGHT,
  DEFAULT_WALL_THICKNESS,
  EDGE_CHOICE_LABEL,
  edgeChoiceOf,
  estacaCabeAqui,
  floorKindsInUse,
  escadaCabeAqui,
  lajeCabeAqui,
  partyWallsTouching,
  radierCabeAqui,
  sameDrawing,
  typologiesOfDrawing,
  wallsOnEdge,
  wallShortName,
} from './typology-draft';
import type { EdgeChoice, TypologyDraft, WorkspaceAction, WorkspaceDraft } from './typology-draft';
import { NumeroInput } from './campos';
import { nomeDaTipologia } from './area-unica';
import { candidatasParaCasar, type CandidataParaCasar } from './casar-paredes';
import { COR_DA_ESTACA, COR_DA_ESTACA_MARCADA } from './estacas-da-prancha';
import {
  DesenharPavimento,
  EmEstaca,
  EstacaSelecionada,
  EstacasDesenhadas,
  TiposDaObra,
} from './EstacasNaPrancha';
import { useEstacasNaPrancha } from './use-estacas-na-prancha';
import { PecasDaPlanilhaDaFerramenta, PecasDaPlanilhaDesenhadas } from './PecasDaPlanilhaNaPrancha';
import { LEGENDA_DA_EXECUCAO, proximaDaFila, tipoDaFerramenta } from './pecas-da-planilha';
import type { PecasDaPlanilhaDaMesa } from './use-pecas-da-planilha';
import { centroDoContorno, COR_DO_BALDRAME, COR_DO_BLOCO, pontosDe } from './fundacao-da-prancha';
import { metros } from './formato';
import {
  BaldrameSelecionado,
  BlocoSelecionado,
  BotoesDaFundacao,
  EmBaldrame,
  EmBloco,
  FundacaoDesenhada,
  FundacaoEmTraco,
} from './FundacaoNaPrancha';
import { useFundacaoNaPrancha, type PecaDaFundacao } from './use-fundacao-na-prancha';
import { FormaDaPeca, PecaEmTraco } from './PecaLivreNaPrancha';
import { TipoEFatoresDoElemento } from './PilarEVigaNaPrancha';
import type { ServicosAprovados } from './servicos-aprovados';
import { areaDoContorno, contornoDoRetangulo, lerContorno } from './pilar-livre-da-prancha';
import {
  usePecaLivreNaPrancha,
  type FormatoDaPeca,
  type PecaLivreNaPrancha,
} from './use-peca-livre-na-prancha';
import { ALTURA_DO_PILAR_MAXIMA, useConfirmacaoDeValorGrande } from './valor-fora-do-comum';
import { COR_DO_MURO, COR_DO_PILAR_DE_CONTENCAO } from './contencao-da-prancha';
import {
  BotoesDaContencao,
  ContencaoDesenhada,
  ContencaoEmTraco,
  EmMuro,
  EmPilarDeContencao,
  MuroSelecionado,
  PilarDeContencaoSelecionado,
} from './ContencaoNaPrancha';
import { useContencaoNaPrancha, type PecaDaContencao } from './use-contencao-na-prancha';
import { COR_DA_LAJE, COR_DO_RADIER } from './laje-da-prancha';
import {
  BotoesDaLaje,
  EmLaje,
  EmRegiao,
  LajeEmTraco,
  LajesDesenhadas,
  LajeSelecionada,
  RegiaoSelecionada,
} from './LajeNaPrancha';
import { useLajeNaPrancha, type PecaDaLaje } from './use-laje-na-prancha';
import { COR_DA_ESCADA } from './escada-da-prancha';
import {
  BotoesDaEscada,
  EmEscada,
  EmParteDaEscada,
  EscadaEmTraco,
  EscadaSelecionada,
  EscadasDesenhadas,
  LanceSelecionado,
  PatamarSelecionado,
} from './EscadaNaPrancha';
import { useEscadaNaPrancha, type PecaDaEscada } from './use-escada-na-prancha';
import { avisoDeQuemAnexou, dataDaPrancha, origemDaPrancha } from './prancha-do-pavimento';
import { renderizarPrancha } from './renderizar-prancha';
import type { PranchaDaMesa } from './use-prancha-do-pavimento';

/**
 * O traçado sobre a planta, em tela cheia.
 *
 * A prancha ocupa a esquerda e o que ela produz fica à direita: o ambiente sendo traçado
 * lado a lado com as medidas que vão saindo, e as paredes de quem já foi traçado. Ver o
 * número nascer enquanto se clica é o que permite perceber o erro na hora — uma parede de
 * 0,40 m no meio de um quarto é óbvia na lista e invisível no desenho.
 *
 * O desenho é um `svg` por cima da imagem, e não um canvas: o clique já vem em coordenadas
 * do elemento, os vértices são elementos de verdade e redesenhar é o React fazendo o que
 * ele faz.
 */

const SELECT_CLASS =
  'h-11 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9';

const ROOM_KINDS = Object.keys(ROOM_KIND_LABEL) as readonly RoomKind[];

type Modo =
  | 'PARADO'
  | 'CALIBRANDO'
  | 'TRACANDO'
  | 'ESTRUTURA'
  | 'VAO'
  | 'SHAFT'
  | 'ESTACA'
  | 'BLOCO'
  | 'BALDRAME'
  | 'ARRANQUE'
  | 'RADIER'
  | 'LAJE'
  | 'REGIAO'
  | 'MURO'
  | 'PILAR_CONTENCAO'
  | 'ESCADA'
  | 'LANCE'
  | 'PATAMAR';

/**
 * O que está selecionado na prancha para excluir: um vão, um pilar ou viga, um shaft, uma
 * estaca, um bloco, um baldrame, um radier ou laje, ou uma região deles (Fase 27).
 */
type PecaSelecionada =
  | { readonly tipo: 'VAO'; readonly id: OpeningId }
  | { readonly tipo: 'PECA'; readonly id: StructuralElementId }
  | { readonly tipo: 'SHAFT'; readonly id: ShaftId }
  | { readonly tipo: 'ESTACA'; readonly id: StructuralPieceId }
  | PecaDaFundacao
  | PecaDaLaje
  | PecaDaContencao
  | PecaDaEscada;

/** O que apaga cada peça selecionada — a lixeira da lista e a tecla Delete dizem o mesmo. */
function acaoDeExcluir(peca: PecaSelecionada): WorkspaceAction {
  switch (peca.tipo) {
    case 'VAO':
      return { type: 'REMOVE_OPENING', openingId: peca.id };
    case 'PECA':
      return { type: 'REMOVE_ELEMENT', elementId: peca.id };
    case 'SHAFT':
      return { type: 'REMOVE_SHAFT', shaftId: peca.id };
    case 'ESTACA':
      return { type: 'REMOVE_PILE', pieceId: peca.id };
    case 'BLOCO':
      return { type: 'REMOVE_PILE_CAP', pieceId: peca.id };
    case 'BALDRAME':
      return { type: 'REMOVE_GROUND_BEAM', pieceId: peca.id };
    case 'LAJE':
      return { type: 'REMOVE_SLAB', pieceId: peca.id };
    case 'REGIAO':
      return { type: 'REMOVE_SLAB_REGION', pieceId: peca.id, index: peca.indice };
    case 'MURO':
      return { type: 'REMOVE_RETAINING_WALL', pieceId: peca.id };
    case 'PILAR_CONTENCAO':
      return { type: 'REMOVE_RETAINING_COLUMN', pieceId: peca.id };
    case 'ESCADA':
      return { type: 'REMOVE_STAIR', pieceId: peca.id };
    case 'LANCE':
      return { type: 'REMOVE_STAIR_FLIGHT', pieceId: peca.id, index: peca.indice };
    case 'PATAMAR':
      return { type: 'REMOVE_STAIR_LANDING', pieceId: peca.id, index: peca.indice };
  }
}

/** A medida com que cada tipo de vão nasce — a mesma do cartão da parede. */
const MEDIDA_DO_VAO: Readonly<Record<OpeningKind, { largura: string; altura: string }>> = {
  PORTA: { largura: '0,8', altura: '2,1' },
  JANELA: { largura: '1,2', altura: '1,2' },
  PASSAGEM: { largura: '0,8', altura: '2,1' },
};

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 8;

/**
 * A folga do encaixe, em pixels **de tela**.
 *
 * Ela existe para perdoar o pulso que treme, e o pulso treme em pixels de tela — não da
 * prancha. Por isso ela é dividida pelo zoom na hora de virar coordenada da prancha: é o
 * que faz ampliar comprar precisão de verdade, em vez de só aumentar o borrão junto com
 * a tolerância.
 */
const FOLGA_VERTICE = 10;
const FOLGA_ORTOGONAL = 8;
/** O vão mira a parede, que é uma faixa: um pouco mais de folga que o canto. */
const FOLGA_VAO = 14;

export function PlanTracer({
  workspace,
  draft,
  dispatch,
  prancha,
  projectId,
  pavimentosSemDesenho,
  aoDesenharPavimento,
  pecasDaPlanilha,
  mediaDoApartamento,
  aprovados,
}: {
  /**
   * A bancada inteira, e não só a tipologia da mesa.
   *
   * A prancha é do **pavimento**: o apto 01, o 02 e a circulação são traçados sobre a
   * mesma folha, e é isso que faz a divisa entre eles aparecer por geometria. Com uma
   * tipologia só à vista, desenhar o andar inteiro era fechar o desenho, trocar a
   * tipologia na página, reabrir a prancha e reencontrar o lugar onde se estava.
   */
  workspace: WorkspaceDraft;
  draft: TypologyDraft;
  dispatch: React.Dispatch<WorkspaceAction>;
  /**
   * A prancha do desenho, guardada na obra (Fase 24).
   *
   * Ausente, a planta trabalha só com o que estiver na bancada, sem guardar nada — é
   * como os testes do desenho a montam, e eles não têm servidor a quem perguntar.
   */
  prancha?: PranchaDaMesa;
  /** A obra, para os tipos de estaca nascerem dela (Fase 27). Ausente, vale a dos tipos que já há. */
  projectId?: ProjectId;
  /**
   * Os pavimentos de Fundação ou Contenção da obra que ainda não têm onde desenhar (F4).
   * Quem vê a estrutura inteira sabe; ausente, vale o que a bancada enxerga — e a bancada
   * só vê os pavimentos que já têm desenho.
   */
  pavimentosSemDesenho?: readonly FloorPlanKind[];
  /**
   * Cria no servidor o desenho do pavimento de Fundação ou Contenção que ainda não tem um
   * (F4). Ausente, a tipologia nasce só na bancada.
   */
  aoDesenharPavimento?: (kind: FloorPlanKind) => void;
  /**
   * As peças da planilha da tipologia, com o lugar e a execução (Fase 28, etapa 3). Ausente, a
   * tipologia não tem planilha por peça — ou os testes do desenho não têm servidor.
   */
  pecasDaPlanilha?: PecasDaPlanilhaDaMesa | undefined;
  /**
   * A média das obras marcadas (`useMediaDoApartamento`), a mesma da aba dos itens: o card do
   * bloco, do baldrame e do radier mostra a da fundação ao lado do fator e da bitola (Fase 41,
   * RN-554); o do pilar, da viga, da escada e da contenção, a da estrutura (Fase 42, RN-584). O
   * tipo de bloco, de pilar e de viga novo nasce com ela (RN-582). Ausente, os cards são os de
   * antes.
   */
  mediaDoApartamento?: ApartmentAverageForProject | undefined;
  /**
   * Os serviços aprovados no orçamento (`useServicosAprovados`): o card do pilar e da viga trava a
   * taxa de aço que o aprovado lê (Fase 42, RN-570). Ausente, a tela não trava — o servidor segura.
   */
  aprovados?: ServicosAprovados | undefined;
}) {
  const [aberto, setAberto] = useState(false);
  /** Há uma parede na mão? Só a prancha sabe, e o diálogo precisa saber para não fechar. */
  const empurrando = useRef(false);
  const plan = workspace.plan;
  // Com a prancha no servidor, o cartão fala pelo registro: a imagem só vem quando a
  // planta abre, e quem veio ver outra coisa não paga os megabytes do PDF.
  const anexada = prancha?.anexada;
  const temPrancha = prancha === undefined ? plan !== undefined : anexada !== undefined;
  const escala =
    prancha === undefined ? plan?.metresPerPixel : anexada?.calibration?.metresPerPixel;
  const tracados = draft.rooms.filter((room) => room.outline !== undefined).length;
  const doPavimento = typologiesOfDrawing(workspace, draft);
  const comDesenho = doPavimento.filter((typology) =>
    typology.rooms.some((room) => room.outline !== undefined),
  ).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Planta</CardTitle>
        <CardDescription>
          Carregue a prancha, calibre numa cota conhecida e trace o contorno de cada ambiente. Área,
          perímetro, paredes e a divisa com o vizinho saem do traço — e pilar e viga também, com as
          paredes em que eles caem. O andar inteiro cabe na mesma folha: a tipologia se troca por
          dentro da prancha.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2">
        <Dialog
          open={aberto}
          onOpenChange={(abrir) => {
            setAberto(abrir);
            if (abrir) prancha?.abrir();
          }}
        >
          <DialogTrigger asChild>
            <Button variant="secondary">
              <Maximize2 className="size-4" />
              {temPrancha ? 'Traçar sobre a planta' : 'Abrir a planta'}
            </Button>
          </DialogTrigger>
          {/*
            A altura cresce com o seletor de tipologia, em vez de dividir com ele o que a
            prancha já tinha: o desenho é o trabalho, e encolhê-lo para caber um cabeçalho
            seria cobrar do traço o preço de uma comodidade.
          */}
          <DialogContent
            className="flex h-[calc(90vh+4.5rem)] max-h-[97vh] w-[90vw] max-w-none flex-col overflow-hidden sm:w-[90vw]"
            /*
              Com uma parede na mão, Esc desiste do empurrão — e **não** fecha a prancha.
              A tecla é a mesma para as duas coisas, e quem errou o gesto não pode perder
              a tela de trabalho junto; só a prancha sabe se há um gesto em curso, e diz
              por este ref.
            */
            onEscapeKeyDown={(evento) => {
              if (empurrando.current) evento.preventDefault();
            }}
          >
            <TracadoEmTelaCheia
              workspace={workspace}
              draft={draft}
              dispatch={dispatch}
              empurrando={empurrando}
              {...(prancha === undefined ? {} : { prancha })}
              {...(projectId === undefined ? {} : { projectId })}
              pecasDaPlanilha={pecasDaPlanilha}
              mediaDoApartamento={mediaDoApartamento}
              aprovados={aprovados}
            />
          </DialogContent>
        </Dialog>

        {/*
          A estaca mora na área comum do desenho de Fundação ou Contenção (F3): quem está
          nela encontra o cadastro de tipos aqui, sem precisar abrir a prancha — o tipo não
          depende de planta nenhuma.
        */}
        {estacaCabeAqui(draft) && (
          <TiposDaObra workspace={workspace} projectId={projectId} dispatch={dispatch} />
        )}
        <DesenharPavimento
          workspace={workspace}
          faltam={pavimentosSemDesenho}
          dispatch={dispatch}
          aoDesenhar={aoDesenharPavimento}
        />

        {!temPrancha ? (
          <span className="text-sm text-(--color-content-muted)">
            {prancha?.falha ??
              (prancha?.carregando === true
                ? 'Procurando a prancha deste pavimento…'
                : 'Nenhuma prancha carregada — os ambientes seguem sendo digitados à mão.')}
          </span>
        ) : (
          <>
            {escala === undefined ? (
              <Badge tone="warn">sem escala</Badge>
            ) : (
              <Badge tone="ok">1 px = {escala.toFixed(4)} m</Badge>
            )}
            {anexada !== undefined && <QuemAnexou anexada={anexada} />}
            <span className="text-sm text-(--color-content-muted)">
              {tracados} {tracados === 1 ? 'ambiente traçado' : 'ambientes traçados'} em{' '}
              {nomeDaTipologia(draft)}
              {/* O que falta do andar é a pergunta de quem desenha o pavimento inteiro. */}
              {doPavimento.length > 1 &&
                ` · ${String(comDesenho)} de ${String(doPavimento.length)} tipologias do pavimento na planta`}
            </span>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Quem pôs a prancha lá, e quando — e de onde ela veio, quando não é o original (RN-248).
 *
 * Fica à vista, e não só no aviso de remover: é a primeira pergunta de quem abre uma
 * planta que não carregou, e a resposta muda o que a pessoa faz em seguida.
 */
function QuemAnexou({ anexada }: { anexada: NonNullable<PranchaDaMesa['anexada']> }) {
  const origem = origemDaPrancha(anexada.source);
  return (
    <span className="text-xs text-(--color-content-muted)">
      Anexada por {anexada.attachedByName} em {dataDaPrancha(anexada.attachedAt)}
      {origem !== undefined && ` · ${origem}`}
    </span>
  );
}

// ── A tela cheia ────────────────────────────────────────────────────────────────

function TracadoEmTelaCheia({
  workspace,
  draft,
  dispatch,
  empurrando,
  prancha: daObra,
  projectId,
  pecasDaPlanilha,
  mediaDoApartamento,
  aprovados,
}: {
  workspace: WorkspaceDraft;
  draft: TypologyDraft;
  dispatch: React.Dispatch<WorkspaceAction>;
  /** Avisa o diálogo de que há um gesto em curso, para o Esc não fechar a prancha. */
  empurrando: React.MutableRefObject<boolean>;
  /** A prancha guardada na obra — ver `PlanTracer`. Aqui, `prancha` é a caixa que rola. */
  prancha?: PranchaDaMesa;
  projectId?: ProjectId;
  /** As peças da planilha da tipologia — ver `PlanTracer`. */
  pecasDaPlanilha?: PecasDaPlanilhaDaMesa | undefined;
  /** A média das obras marcadas, para os cards da fundação e da estrutura — ver `PlanTracer`. */
  mediaDoApartamento?: ApartmentAverageForProject | undefined;
  /** Os serviços aprovados, para a trava do aço do pilar e da viga — ver `PlanTracer`. */
  aprovados?: ServicosAprovados | undefined;
}) {
  const plan = workspace.plan;
  const anexada = daObra?.anexada;
  const temPrancha = daObra === undefined ? plan !== undefined : anexada !== undefined;
  const [modo, setModo] = useState<Modo>('PARADO');
  /**
   * A peça da planilha que o próximo clique posiciona (RN-429). Enquanto ela espera, nenhuma
   * ferramenta começa nada na prancha: o clique é dela.
   */
  const [posicionandoPeca, setPosicionandoPeca] = useState<string | undefined>(undefined);
  // Só vale enquanto a ferramenta for a do tipo dela: trocar de ferramenta larga a peça, sem
  // que o clique seguinte a ponha no lugar de outra coisa.
  const pecaNaMao =
    posicionandoPeca !== undefined &&
    pecasDaPlanilha?.pecas.find((peca) => peca.name === posicionandoPeca)?.kind ===
      tipoDaFerramenta(modo)
      ? posicionandoPeca
      : undefined;
  // O clique que sobe depois de pegar um marcador é do gesto de pegar, e não o lugar novo dele.
  const pegouMarcador = useRef(false);
  // A recusa é da ação que falhou: ao trocar de ferramenta ela sai da tela, em vez de aparecer
  // no painel da próxima (o "muro precisa de dois pontos" que sobrava no painel da Escada).
  // Lê o erro no instante da troca, e por isso só `modo` observa — erro novo não se apaga.
  const erroNoRascunho = useRef<string | undefined>(undefined);
  erroNoRascunho.current = draft.lastError ?? workspace.lastError;
  useEffect(() => {
    if (erroNoRascunho.current !== undefined) dispatch({ type: 'DISMISS_ERROR' });
  }, [modo, dispatch]);
  const [calibragem, setCalibragem] = useState<readonly Pixel[]>([]);
  const [medidaReal, setMedidaReal] = useState('3');
  const [tracado, setTracado] = useState<readonly Pixel[]>([]);
  const [nome, setNome] = useState('');
  const [kind, setKind] = useState<RoomKind>('QUARTO');
  const [altura, setAltura] = useState(String(DEFAULT_WALL_HEIGHT).replace('.', ','));
  const [peDireito, setPeDireito] = useState('2,6');
  const [selecionado, setSelecionado] = useState<RoomId | undefined>(undefined);
  /**
   * O ambiente que está sendo delimitado — `undefined` é traçado de ambiente **novo**.
   *
   * São dois caminhos de verdade, e não um só com variação: quem chega pela planta cria o
   * ambiente traçando; quem já cadastrou a Sala vem à prancha só para dizer onde ela fica.
   * Sem a distinção, o segundo acabava com duas Salas.
   */
  const [delimitando, setDelimitando] = useState<RoomId | undefined>(undefined);
  /**
   * A peça sendo traçada: o tipo, os dois cantos opostos e a medida que a planta não vê.
   *
   * Pilar e viga entram **arrastando** o retângulo — ou por dois cliques nos cantos, que é
   * o mesmo gesto para quem prefere mirar. A planta é vista de cima, e o que ela mostra é
   * a seção do pilar e a corrida da viga. O que falta em cada um sobe para fora do papel:
   * a altura do pilar e a altura da seção da viga.
   */
  const [pecaKind, setPecaKind] = useState<StructuralElementKind>('PILAR');
  const [cantos, setCantos] = useState<readonly Pixel[]>([]);
  /** O canto em que o botão desceu, enquanto ele não sobe. Ausente é ninguém arrastando. */
  const [arrasto, setArrasto] = useState<{ readonly origem: Pixel } | undefined>(undefined);
  /**
   * A parede pega pelo duplo clique, andando de lado até o próximo clique.
   *
   * Fica solta do `Modo` de propósito: pegar a parede é um conserto rápido no meio de
   * qualquer coisa, e obrigar a entrar num modo para dez centímetros seria trocar um
   * clique por três.
   */
  const [movendo, setMovendo] = useState<
    { readonly alvo: EdgeRef; readonly origem: Point; readonly distancia: number } | undefined
  >(undefined);
  /** Onde o cursor está enquanto se traça — o segmento vivo, que denuncia o traço torto. */
  const [cursor, setCursor] = useState<Pixel | undefined>(undefined);
  const [medidaDaPeca, setMedidaDaPeca] = useState('2,9');
  /** Metros — a largura da viga em polígono: é ela que define as pontas do contorno. */
  const [larguraDaViga, setLarguraDaViga] = useState('0,2');
  const [erro, setErro] = useState<string | undefined>(undefined);
  const [zoom, setZoom] = useState(1);
  /**
   * Os nomes das paredes, ligados de saída.
   *
   * Reconhecer a parede no desenho é o trabalho todo de quem confere o quantitativo — a
   * lista à direita diz "Sala · Lado 3" e, sem o rótulo na prancha, achar esse lado é
   * contar os lados na mão. Desligável porque prancha cheia com o zoom afastado vira
   * mancha de texto, e aí o desenho some atrás do que devia explicá-lo.
   */
  const [mostrarNomes, setMostrarNomes] = useState(true);
  /**
   * Remover a prancha pede confirmação — e trocar também, desde que ela é da obra.
   *
   * Removida, a volta é carregar o PDF de novo e recalibrar; e a prancha na tela pode ser
   * a que outra pessoa anexou, que é o que o aviso diz com o nome dela (RN-246). Um clique
   * solto no botão ao lado do zoom não pode custar isso.
   */
  const [confirmandoRemocao, setConfirmandoRemocao] = useState(false);
  const [confirmandoTroca, setConfirmandoTroca] = useState(false);
  /** O que deu certo com ressalva — a página renderizada no lugar do PDF grande (RN-248). */
  const [aviso, setAviso] = useState<string | undefined>(undefined);
  /** O vão que o próximo clique numa parede vai pôr: o tipo e a medida digitada. */
  const [vaoKind, setVaoKind] = useState<OpeningKind>('PORTA');
  const [vaoLargura, setVaoLargura] = useState(MEDIDA_DO_VAO.PORTA.largura);
  const [vaoAltura, setVaoAltura] = useState(MEDIDA_DO_VAO.PORTA.altura);
  const [pecaSelecionada, setPecaSelecionada] = useState<PecaSelecionada | undefined>(undefined);
  /**
   * A parede na mão — escolhida no painel do ambiente ou clicada na prancha (Fase 24, D24).
   *
   * É dela que sai o "Casar com…": a lista das paredes livres que correm junto desta, da
   * mais perto para a mais longe, e o "Descasar" da que já tem vizinho (RN-306).
   */
  const [paredeSelecionada, setParedeSelecionada] = useState<WallId | undefined>(undefined);
  /** Quantos shafts iguais o próximo traço cadastra — os dois de 60 × 40 lado a lado. */
  const [shaftsIguais, setShaftsIguais] = useState('1');
  /** O ambiente à espera da confirmação para sair — ele leva paredes, vãos e peças junto. */
  const [ambienteAExcluir, setAmbienteAExcluir] = useState<RoomId | undefined>(undefined);
  /**
   * O lado que vai deixar de ser parede, à espera da confirmação (D25).
   *
   * Só quando a parede que sai leva alguma coisa junto: a porta e o pilar não têm onde
   * ficar num guarda-corpo, e voltar o lado para parede não os traz de volta.
   */
  const [ladoATrocar, setLadoATrocar] = useState<
    | {
        readonly roomId: RoomId;
        readonly edgeIndex: number;
        readonly kind: EdgeChoice;
        readonly vaos: number;
        readonly pecas: number;
      }
    | undefined
  >(undefined);
  const entrada = useRef<HTMLInputElement>(null);
  const prancha = useRef<HTMLDivElement>(null);

  /**
   * Amplia mantendo firme o ponto que a pessoa está olhando.
   *
   * Sem o ajuste de rolagem, ampliar joga a prancha para um canto e quem estava mirando o
   * canto de um quarto tem de reencontrá-lo a cada passo — que é o mesmo que não ter zoom.
   */
  const aplicarZoom = useCallback(
    (alvo: number, foco?: { readonly x: number; readonly y: number }): void => {
      const caixa = prancha.current;
      const novo = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, alvo));
      if (caixa === null) {
        setZoom(novo);
        return;
      }

      const dentroX = foco?.x ?? caixa.clientWidth / 2;
      const dentroY = foco?.y ?? caixa.clientHeight / 2;
      const conteudoX = caixa.scrollLeft + dentroX;
      const conteudoY = caixa.scrollTop + dentroY;

      setZoom((atual) => {
        const razao = novo / atual;
        requestAnimationFrame(() => {
          caixa.scrollLeft = conteudoX * razao - dentroX;
          caixa.scrollTop = conteudoY * razao - dentroY;
        });
        return novo;
      });
    },
    [],
  );

  // Ctrl/⌘ + roda é o gesto que se espera sobre uma planta. Precisa de listener **não
  // passivo** para cancelar o zoom da página inteira, e o `onWheel` do React é passivo.
  useEffect(() => {
    const caixa = prancha.current;
    if (caixa === null) return;

    const aoGirar = (evento: WheelEvent): void => {
      if (!evento.ctrlKey && !evento.metaKey) return;
      evento.preventDefault();
      const limites = caixa.getBoundingClientRect();
      aplicarZoom(zoom * (evento.deltaY < 0 ? 1.1 : 1 / 1.1), {
        x: evento.clientX - limites.left,
        y: evento.clientY - limites.top,
      });
    };

    caixa.addEventListener('wheel', aoGirar, { passive: false });
    return () => {
      caixa.removeEventListener('wheel', aoGirar);
    };
  }, [aplicarZoom, zoom]);

  /** Cabe inteira na largura disponível — o ponto de partida de quem acabou de abrir. */
  function ajustar(): void {
    const caixa = prancha.current;
    if (caixa === null || plan === undefined) return;
    aplicarZoom((caixa.clientWidth - 8) / plan.width);
  }

  /**
   * A prancha recém-carregada já abre inteira na tela.
   *
   * O PDF é rasterizado a até 3000 px de largura, e a 100% o que aparece é um canto dele:
   * a cota que a calibragem pede — o primeiro passo — ficava fora de vista, e quem acabou
   * de carregar não sabia para onde rolar. Só na carga: quem ajustou o zoom à mão não o
   * perde a cada recalibragem.
   */
  const ajustarAoCarregar = useRef(false);
  const larguraDaPrancha = plan?.width;
  useEffect(() => {
    const caixa = prancha.current;
    if (!ajustarAoCarregar.current || caixa === null || larguraDaPrancha === undefined) return;
    ajustarAoCarregar.current = false;
    aplicarZoom((caixa.clientWidth - 8) / larguraDaPrancha);
  }, [plan?.image, larguraDaPrancha, aplicarZoom]);

  const emDelimitacao =
    delimitando === undefined ? undefined : draft.rooms.find((room) => room.id === delimitando);

  const escala = plan?.metresPerPixel;

  /**
   * As outras tipologias **do mesmo pavimento**, que é o que esta prancha mostra.
   *
   * Do mesmo pavimento, e não todas: o garden foi traçado sobre outra folha, e os
   * contornos dele cairiam em cima desta como se fossem daqui. Aparecem apagadas, atrás
   * do que está sendo desenhado — é o vizinho, não o trabalho.
   */
  const irmas = useMemo(
    () =>
      typologiesOfDrawing(workspace, draft).filter(
        (typology) => typology.unitTypeId !== draft.unitTypeId,
      ),
    [workspace, draft.floorKind, draft.unitTypeId],
  );

  /**
   * Todos os vértices já traçados, em pixels — o alvo do encaixe.
   *
   * **Inclui os das vizinhas.** É o que faz a divisa entre o apto 01 e o 02 fechar
   * exatamente no mesmo ponto: sem isso, o canto do vizinho ficava a três pixels do canto
   * novo, e a parede que os dois dividem passava a medir 3,997 m de um lado e 4,00 do
   * outro — dois cantos onde havia um.
   */
  const verticesExistentes = useMemo((): readonly Pixel[] => {
    if (escala === undefined) return [];
    return [draft, ...irmas].flatMap((typology) =>
      typology.rooms.flatMap((room) =>
        (room.outline ?? []).map((ponto) => metresToPixel(ponto, escala)),
      ),
    );
  }, [draft, irmas, escala]);

  /**
   * Trocar a tipologia na mesa **descarta o traço pela metade**.
   *
   * O que está clicado pertence a quem estava sendo desenhado: aproveitá-lo na tipologia
   * seguinte criaria a Sala do apto 02 com os cantos do 01, e ninguém veria — o ambiente
   * nasceria com a área certa no lugar errado. O modo continua: quem estava traçando
   * segue traçando, agora do outro apto.
   */
  const emDesenho = draft.unitTypeId;
  useEffect(() => {
    setTracado([]);
    setCantos([]);
    setArrasto(undefined);
    setSelecionado(undefined);
    setDelimitando(undefined);
    setParedeSelecionada(undefined);
    // A peça escolhida é da tipologia que saiu da mesa; o Delete a procuraria na errada.
    setPecaSelecionada(undefined);
  }, [emDesenho]);

  /**
   * Esc desiste do empurrão — e **só** dele.
   *
   * A mesma tecla fecharia a prancha inteira, que é o que o diálogo faz com ela: quem
   * errou o gesto perderia a tela de trabalho junto. Na fase de captura, portanto, e com
   * o evento parado antes de chegar ao diálogo.
   */
  useEffect(() => {
    empurrando.current = movendo !== undefined;
    if (movendo === undefined) return;

    const aoTeclar = (evento: KeyboardEvent): void => {
      if (evento.key !== 'Escape') return;
      evento.preventDefault();
      evento.stopPropagation();
      setMovendo(undefined);
    };

    document.addEventListener('keydown', aoTeclar, true);
    return () => {
      document.removeEventListener('keydown', aoTeclar, true);
    };
  }, [movendo, empurrando]);

  useEffect(() => {
    if (pecaSelecionada === undefined) return;
    const aoTeclar = (evento: KeyboardEvent): void => {
      if (evento.key !== 'Delete') return;
      const alvo = evento.target as HTMLElement | null;
      if (alvo !== null && ['INPUT', 'TEXTAREA', 'SELECT'].includes(alvo.tagName)) return;
      evento.preventDefault();
      dispatch(acaoDeExcluir(pecaSelecionada));
      setPecaSelecionada(undefined);
    };
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [pecaSelecionada, dispatch]);

  async function carregar(arquivo: File): Promise<void> {
    setErro(undefined);
    setAviso(undefined);
    if (daObra !== undefined) {
      // Renderizar, subir e pôr na mesa é de quem guarda a prancha; a planta só diz o que
      // aconteceu.
      const resultado = await daObra.anexar(arquivo);
      if (!resultado.ok) {
        setErro(resultado.erro);
        return;
      }
      if (resultado.aviso !== undefined) setAviso(resultado.aviso);
    } else {
      try {
        dispatch({ type: 'SET_PLAN', plan: await renderizarPrancha(arquivo) });
      } catch {
        setErro('Não consegui abrir esse arquivo. Vale PDF ou imagem da prancha.');
        return;
      }
    }
    ajustarAoCarregar.current = true;
    setModo('CALIBRANDO');
    setCalibragem([]);
  }

  /** Onde o mouse está, em pixel **da prancha** — o zoom não pode entrar na conta. */
  function pontoDaPrancha(evento: MouseEvent<SVGSVGElement>): Pixel {
    const caixa = evento.currentTarget.getBoundingClientRect();
    // A caixa vem **ampliada**, e o contorno é guardado em pixel da prancha. Dividir pelo
    // zoom é o que devolve a unidade certa — sem isso, todo ponto clicado com zoom sairia
    // multiplicado por ele, e o erro não apareceria no desenho: só no orçamento.
    return {
      x: (evento.clientX - caixa.left) / zoom,
      y: (evento.clientY - caixa.top) / zoom,
    };
  }

  /**
   * O canto da peça, encaixado no vértice como o contorno.
   *
   * O pilar é desenhado **no canto** do ambiente, e é justamente ali que ele precisa cair
   * para a parede reconhecê-lo.
   */
  function cantoDaPeca(evento: MouseEvent<SVGSVGElement>): Pixel {
    return resolvePoint(pontoDaPrancha(evento), {
      vertices: verticesExistentes,
      vertexTolerance: FOLGA_VERTICE / zoom,
    });
  }

  /**
   * Duplo clique na parede: ela passa a seguir o mouse, de lado.
   *
   * É o conserto que todo mundo precisa depois de traçar sobre uma prancha — o clique caiu
   * dez centímetros fora, e a alternativa era redesenhar o ambiente inteiro, perdendo a
   * porta, o pilar e o nome de cada parede junto. A parede anda na perpendicular dela
   * porque é assim que parede de planta anda: a de pé vai para os lados, a deitada sobe e
   * desce.
   *
   * Só com a prancha parada: no meio de um traçado, o duplo clique é dois cliques do
   * contorno, e roubá-los quebraria o desenho em andamento.
   */
  function pegarParede(evento: MouseEvent<SVGSVGElement>): void {
    if (modo !== 'PARADO' || escala === undefined) return;
    // Dois cliques seguidos são, para o navegador, um pedido para selecionar texto: sem
    // isto a prancha inteira fica azul de seleção no meio do gesto.
    evento.preventDefault();
    window.getSelection()?.removeAllRanges();

    const emMetros = pixelToMetres(pontoDaPrancha(evento), escala);
    const alvo = edgeAt(emMetros, draft.rooms, (FOLGA_VERTICE / zoom) * escala);
    if (alvo === undefined) return;

    setMovendo({ alvo, origem: emMetros, distancia: 0 });
    setSelecionado(alvo.roomId);
  }

  function moverParede(evento: MouseEvent<SVGSVGElement>): void {
    if (movendo === undefined || escala === undefined) return;
    const atual = pixelToMetres(pontoDaPrancha(evento), escala);
    setMovendo({
      ...movendo,
      distancia: slideDistance(movendo.alvo.segment, movendo.origem, atual),
    });
  }

  /** O que o empurrão vai deixar no lugar — a conta que a prévia desenha e o clique grava. */
  const empurrados = useMemo(
    () =>
      movendo === undefined || movendo.distancia === 0
        ? []
        : slideEdge(draft.rooms, movendo.alvo, movendo.distancia),
    [movendo, draft.rooms],
  );

  /**
   * O ambiente empurrado, antes e depois — a conta que o rótulo mostra.
   *
   * O sinal do empurrão não serve para ninguém: ele é o da normal do polígono, e depende
   * de o contorno ter sido traçado no sentido horário ou não. O que se quer saber é se o
   * ambiente **cresceu ou encolheu**, e quanto ele passa a ter.
   */
  const areaEmpurrada = useMemo(() => {
    if (movendo === undefined) return undefined;
    const antes = draft.rooms.find((room) => room.id === movendo.alvo.roomId)?.outline ?? [];
    const depois =
      empurrados.find((candidato) => candidato.roomId === movendo.alvo.roomId)?.outline ?? antes;
    return { antes: polygonArea(antes), depois: polygonArea(depois) };
  }, [movendo, empurrados, draft.rooms]);

  function soltarParede(): void {
    // O empurrão vale para cada ambiente que divide aquele lado: a divisa anda com os
    // dois, senão sobraria um vazio da espessura do bloco entre eles.
    for (const { roomId, outline } of empurrados) {
      dispatch({ type: 'RESHAPE_ROOM', roomId, outline });
    }
    setMovendo(undefined);
  }

  /**
   * Pilar e viga nascem **arrastados**, e continuam nascendo de dois cliques.
   *
   * Arrastar é o gesto de quem desenha um retângulo, e é o que a mão já sabe fazer: o
   * botão desce num canto, a peça cresce à vista com as medidas do painel mudando junto, e
   * solta no outro canto. Os dois cliques ficaram porque em zoom alto é mais firme mirar
   * dois pontos do que segurar o botão atravessando a tela — e porque quem já tinha o
   * costume não pode descobrir que a ferramenta mudou embaixo dele.
   *
   * O que separa os dois é a distância: soltar onde apertou é clique, e aí o primeiro
   * canto fica marcado esperando o segundo.
   *
   * O shaft nasce pelo mesmo gesto: é um retângulo visto de cima, como o pilar (D21).
   */
  const pecaLivre = usePecaLivreNaPrancha({
    draft,
    escala,
    zoom,
    ativo: modo === 'ESTRUTURA',
    parado: modo === 'PARADO' && movendo === undefined,
    vertices: verticesExistentes,
    dispatch,
  });
  // O polígono de pilar e viga nasce de cliques nos vértices, e não dos dois cantos.
  const porDoisCantos =
    (modo === 'ESTRUTURA' && pecaLivre.formato === 'RETANGULO') || modo === 'SHAFT';

  function apertar(evento: MouseEvent<SVGSVGElement>): void {
    if (pecaNaMao !== undefined) return;
    // O marcador da peça da planilha, na ferramenta do tipo dela: pegá-lo é movê-lo (RN-429).
    const marcador = (evento.target as Element)
      .closest('[data-peca-da-planilha]')
      ?.getAttribute('data-peca-da-planilha');
    const tipoNaMao = tipoDaFerramenta(modo);
    if (
      marcador !== null &&
      marcador !== undefined &&
      tipoNaMao !== undefined &&
      pecasDaPlanilha?.pecas.find((peca) => peca.name === marcador)?.kind === tipoNaMao
    ) {
      pegouMarcador.current = true;
      setPosicionandoPeca(marcador);
      return;
    }
    // A estaca pega pelo botão que desce: se o mouse andar, ela vai junto. No modo Marcar o
    // clique é da marca, e arrastar sem querer a tiraria do lugar.
    const aEstaca = (evento.target as Element)
      .closest('[data-estaca]')
      ?.getAttribute('data-estaca');
    if (
      aEstaca !== null &&
      aEstaca !== undefined &&
      movendo === undefined &&
      (modo === 'PARADO' || (modo === 'ESTACA' && estacas.acao !== 'MARCAR'))
    ) {
      estacas.apertar(aEstaca as StructuralPieceId, pontoDaPrancha(evento));
      return;
    }

    // O bloco e a ponta do baldrame pegam do mesmo jeito; o retângulo do bloco nasce do
    // botão que desce no vazio, e o clique que vem depois decide se foi arrasto ou canto.
    if (movendo === undefined) {
      if (fundacao.apertarNaPeca(evento.target as Element, pontoDaPrancha(evento))) return;
      // O radier e a laje também: arrastar a peça leva as regiões junto.
      if (laje.apertarNaPeca(evento.target as Element, pontoDaPrancha(evento))) return;
      // O muro e o pilar de contenção: a ponta do eixo e o pilar se arrastam parados.
      if (contencao.apertarNaPeca(evento.target as Element, pontoDaPrancha(evento))) return;
      // A escada também: arrastá-la leva os lances e os patamares juntos.
      if (escada.apertarNaEscada(evento.target as Element, pontoDaPrancha(evento))) return;
      // O pilar e a viga com contorno também: arrastá-los leva o contorno junto.
      if (pecaLivre.apertarNaPeca(evento.target as Element, pontoDaPrancha(evento))) return;
      fundacao.apertarNaPrancha(pontoDaPrancha(evento));
      laje.apertarNaPrancha(pontoDaPrancha(evento));
      escada.apertarNaPrancha(pontoDaPrancha(evento));
    }

    if (!porDoisCantos) return;
    const ponto = cantoDaPeca(evento);

    // Com um canto marcado, apertar é o **segundo clique**: a origem continua sendo ele.
    // Sem isso, quem clicou uma vez e resolveu arrastar perdia o canto que tinha mirado.
    const origem = cantos.length === 1 ? cantos[0]! : ponto;
    setArrasto({ origem });
    setCantos(cantos.length === 1 ? [origem, ponto] : [ponto]);
  }

  /** Tudo o que acompanha o mouse: a peça em arrasto, a parede empurrada e o traço vivo. */
  function acompanhar(evento: MouseEvent<SVGSVGElement>): void {
    if (movendo !== undefined) {
      moverParede(evento);
      return;
    }
    if (estacas.arrastando !== undefined) {
      estacas.mover(pontoDaPrancha(evento));
      return;
    }
    if (fundacao.acompanhar(pontoDaPrancha(evento))) return;
    if (laje.acompanhar(pontoDaPrancha(evento))) return;
    if (contencao.acompanhar(pontoDaPrancha(evento))) return;
    if (escada.acompanhar(pontoDaPrancha(evento))) return;
    if (pecaLivre.acompanhar(pontoDaPrancha(evento))) return;
    if (porDoisCantos && arrasto !== undefined) {
      setCantos([arrasto.origem, cantoDaPeca(evento)]);
      return;
    }
    // O segmento vivo, do último ponto até aqui: é ele que serrilha antes de o traço
    // existir, que é quando o aviso ainda serve para alguma coisa.
    if (modo === 'TRACANDO' && tracado.length > 0) setCursor(pontoDaPrancha(evento));
  }

  function soltar(evento: MouseEvent<SVGSVGElement>): void {
    if (estacas.arrastando !== undefined) {
      estacas.soltar(pontoDaPrancha(evento));
      return;
    }
    if (fundacao.soltar(pontoDaPrancha(evento))) return;
    if (laje.soltar(pontoDaPrancha(evento))) return;
    if (contencao.soltar(pontoDaPrancha(evento))) return;
    if (escada.soltar(pontoDaPrancha(evento))) return;
    if (pecaLivre.soltar(pontoDaPrancha(evento))) return;
    if (!porDoisCantos || arrasto === undefined) return;
    const ponto = cantoDaPeca(evento);
    setArrasto(undefined);
    // Soltar em cima de onde apertou não é peça de tamanho zero: é um clique, e o canto
    // fica marcado esperando o próximo.
    setCantos(
      distance(arrasto.origem, ponto) < FOLGA_VERTICE / zoom
        ? [arrasto.origem]
        : [arrasto.origem, ponto],
    );
  }

  function clique(evento: MouseEvent<SVGSVGElement>): void {
    // O botão que sobe depois de arrastar uma estaca dispara um clique: ele é do arrasto.
    if (estacas.foiCliqueDoArrasto()) return;
    if (fundacao.foiCliqueDoArrasto()) return;
    if (laje.foiCliqueDoArrasto()) return;
    if (contencao.foiCliqueDoArrasto()) return;
    if (escada.foiCliqueDoArrasto()) return;
    if (pecaLivre.foiCliqueDoArrasto()) return;
    const bruto = pontoDaPrancha(evento);

    if (pegouMarcador.current) {
      pegouMarcador.current = false;
      return;
    }

    // A peça da planilha na mão: o clique é o lugar dela (RN-429). A que ainda não tinha lugar
    // chama a próxima da fila; mover uma que já estava na planta acaba aqui.
    if (pecaNaMao !== undefined) {
      const naMao = pecasDaPlanilha?.pecas.find((peca) => peca.name === pecaNaMao);
      if (escala !== undefined && pecasDaPlanilha !== undefined) {
        pecasDaPlanilha.posicionar(pecaNaMao, pixelToMetres(bruto, escala));
      }
      setPosicionandoPeca(
        naMao !== undefined && naMao.position === undefined && pecasDaPlanilha !== undefined
          ? proximaDaFila(pecasDaPlanilha.pecas, naMao.kind, pecaNaMao)
          : undefined,
      );
      return;
    }

    // A parede pega pelo duplo clique se solta no clique seguinte — e o gesto acaba aí,
    // sem passar para quem quer que fosse atender este clique.
    if (movendo !== undefined) {
      soltarParede();
      return;
    }

    if (modo === 'CALIBRANDO') {
      // Atualização por função, e não pelo valor do fechamento: dois cliques no mesmo
      // quadro liam o mesmo estado antigo, e o segundo apagava o primeiro em vez de somar.
      setCalibragem((atual) => [...atual, bruto].slice(-2));
      return;
    }

    // A estrutura é resolvida no apertar/soltar: o clique chega **depois** deles, e
    // atendê-lo aqui de novo somaria um terceiro canto a cada peça arrastada.
    if (porDoisCantos) return;

    // O pilar e a viga em polígono: o clique é um vértice do contorno.
    if (modo === 'ESTRUTURA') {
      pecaLivre.clicarNaPrancha(bruto);
      return;
    }

    if (modo === 'VAO') {
      inserirVao(bruto);
      return;
    }

    // O clique é o centro da estaca: marca o ponto, ou posiciona a da fila (D5).
    if (modo === 'ESTACA') {
      estacas.clicarNaPrancha(bruto);
      return;
    }

    // O bloco e o baldrame: o clique é um canto, um vértice ou uma ponta do eixo.
    if (modo === 'BLOCO' || modo === 'BALDRAME') {
      fundacao.clicarNaPrancha(bruto);
      return;
    }

    // O radier, a laje e a região: o clique é um canto ou um vértice do contorno.
    if (modo === 'RADIER' || modo === 'LAJE' || modo === 'REGIAO') {
      laje.clicarNaPrancha(bruto);
      return;
    }

    // O muro e o pilar de contenção: o clique é uma ponta do eixo ou o centro do pilar.
    if (modo === 'MURO' || modo === 'PILAR_CONTENCAO') {
      contencao.clicarNaPrancha(bruto);
      return;
    }

    // A escada, o lance e o patamar: o clique é um canto do retângulo.
    if (modo === 'ESCADA' || modo === 'LANCE' || modo === 'PATAMAR') {
      escada.clicarNaPrancha(bruto);
      return;
    }

    // Parada, o clique no vazio desfaz a seleção. O clique no vão ou na peça não chega
    // aqui: eles o param, justamente para não se desselecionarem.
    if (modo === 'PARADO') {
      setPecaSelecionada(undefined);
      setParedeSelecionada(undefined);
      return;
    }

    if (modo !== 'TRACANDO') return;

    if (closesPolygon(bruto, tracado, FOLGA_VERTICE / zoom)) {
      fechar();
      return;
    }

    setTracado((atual) => {
      const ponto = resolvePoint(bruto, {
        vertices: [...verticesExistentes, ...atual],
        vertexTolerance: FOLGA_VERTICE / zoom,
        orthogonalTolerance: FOLGA_ORTOGONAL / zoom,
        ...(atual.length > 0 ? { previous: atual[atual.length - 1]! } : {}),
      });
      return [...atual, ponto];
    });
  }

  function calibrar(): void {
    const [a, b] = calibragem;
    if (a === undefined || b === undefined) return;

    const fator = metresPerPixel(a, b, Number(medidaReal.replace(',', '.')));
    if (fator === undefined) {
      setErro('Clique dois pontos afastados e informe a medida real entre eles.');
      return;
    }
    if (daObra !== undefined) {
      // A escala vai para a obra antes de reescalar a mesa (RN-247); se não chegar lá, a
      // planta diz, em vez de seguir com uma escala que só esta tela conhece.
      void daObra.calibrar(fator).then((resultado) => {
        if (!resultado.ok) setErro(resultado.erro);
      });
    } else {
      dispatch({ type: 'SET_PLAN_SCALE', metresPerPixel: fator });
    }
    setCalibragem([]);
    setModo('PARADO');
    setErro(undefined);
  }

  function fechar(): void {
    if (escala === undefined || tracado.length < 3) return;
    const outline = tracado.map((pixel) => pixelToMetres(pixel, escala));

    if (delimitando === undefined) {
      dispatch({
        type: 'ADD_TRACED_ROOM',
        name: nome.trim() === '' ? ROOM_KIND_LABEL[kind] : nome,
        kind,
        // A alvenaria sobe até a laje; o pé-direito é o que se enxerga até o forro. O
        // ambiente traçado mede as duas coisas como o digitado à mão.
        wallHeight: Number(altura.replace(',', '.')) || DEFAULT_WALL_HEIGHT,
        ceilingHeight: Number(peDireito.replace(',', '.')) || 2.6,
        outline,
      });
      setNome('');
    } else {
      dispatch({ type: 'SET_ROOM_OUTLINE', roomId: delimitando, outline });
      setSelecionado(delimitando);
      setDelimitando(undefined);
    }

    setTracado([]);
    setModo('PARADO');
  }

  /**
   * Sem escala, quem pede ambiente ou peça é levado à calibragem — e não a um botão mudo.
   *
   * O botão desabilitado era um beco: carregar o PDF já abre a calibragem, o "Calibrar
   * escala" aceso parece o passo seguinte, e clicá-lo **desliga** a calibragem. Dali em
   * diante "Novo ambiente" ficava apagado sem dizer por quê, e o ambiente não havia como
   * nascer. Sem metro por pixel não há medida, então o caminho é mesmo a calibragem; o
   * que muda é que agora o clique leva até ela.
   */
  function pedirEscala(): void {
    setModo('CALIBRANDO');
    setCalibragem([]);
    setTracado([]);
    setCantos([]);
    setArrasto(undefined);
    setDelimitando(undefined);
  }

  /**
   * O vão entra na parede clicada, centrado no clique.
   *
   * O modo continua depois: uma fachada tem quatro janelas iguais, e voltar ao botão a cada
   * uma seria trocar um clique por três. Clicar fora de parede não inventa parede — diz.
   */
  function inserirVao(bruto: Pixel): void {
    if (escala === undefined) return;
    const alvo = wallAt(
      pixelToMetres(bruto, escala),
      paredesColocadas,
      (FOLGA_VAO / zoom) * escala,
    );
    if (alvo === undefined) {
      setErro('Clique sobre uma parede para pôr o vão.');
      return;
    }
    const largura = Number(vaoLargura.replace(',', '.'));
    const altura = Number(vaoAltura.replace(',', '.'));
    if (!(largura > 0) || !(altura > 0)) {
      setErro('Informe a largura e a altura do vão.');
      return;
    }
    dispatch({
      type: 'ADD_OPENING',
      wallId: alvo.wallId,
      kind: vaoKind,
      width: largura,
      height: altura,
      offset: alvo.offset,
    });
    setErro(undefined);
  }

  function excluirPeca(peca: PecaSelecionada): void {
    dispatch(acaoDeExcluir(peca));
    setPecaSelecionada(undefined);
  }

  /**
   * O lado vira outra coisa — e, se a parede que sai leva vão ou peça, pergunta antes.
   *
   * Parede e mureta nunca perguntam: ou a parede já está lá, ou nasce nova. Guarda-corpo e
   * aberto tiram a parede daquele lado, e sem vão nem peça nela não há o que perder.
   */
  function trocarLado(roomId: RoomId, edgeIndex: number, kind: EdgeChoice): void {
    const room = draft.rooms.find((candidate) => candidate.id === roomId);
    if (room === undefined) return;
    if (kind === 'GUARDA_CORPO' || kind === 'ABERTO') {
      const saem = new Set(
        wallsOnEdge(room, draft.walls, edgeIndex).map((wall) => String(wall.id)),
      );
      const vaos = draft.openings.filter((opening) => saem.has(String(opening.wallId))).length;
      const pecas = new Set(
        draft.elementSegments
          .filter((segment) => saem.has(String(segment.wallId)))
          .map((segment) => String(segment.elementId)),
      ).size;
      if (vaos + pecas > 0) {
        setLadoATrocar({ roomId, edgeIndex, kind, vaos, pecas });
        return;
      }
    }
    dispatch({ type: 'SET_EDGE_KIND', roomId, edgeIndex, kind });
  }

  /** Começa a delimitar um ambiente que já existe — o caminho de quem escolhe à direita. */
  function delimitar(roomId: RoomId): void {
    setDelimitando(roomId);
    setSelecionado(roomId);
    setTracado([]);
    setModo('TRACANDO');
  }

  /** Onde cada parede desta tipologia está na prancha — o alvo do traço da peça. */
  const paredesColocadas = useMemo(
    () => placeWalls(draft.rooms, draft.walls),
    [draft.rooms, draft.walls],
  );

  /** A parede pela identidade, para o desenho saber a espessura e se ela é conjugada. */
  const paredePorId = useMemo(
    () => new Map(draft.walls.map((wall) => [String(wall.id), wall])),
    [draft.walls],
  );

  /**
   * As candidatas a casar com uma parede: da tipologia da mesa e das vizinhas do mesmo
   * pavimento, que estão nesta prancha (RN-306).
   */
  const casarCom = useCallback(
    (wall: Wall): readonly CandidataParaCasar[] =>
      candidatasParaCasar([draft, ...irmas], draft, wall),
    [draft, irmas],
  );

  /** A parede clicada na prancha fica na mão, e o ambiente dela abre no painel. */
  function selecionarParede(posta: PlacedWall): void {
    setParedeSelecionada(posta.wallId);
    setSelecionado(posta.roomId);
    setPecaSelecionada(undefined);
  }

  /**
   * O vão e a estrutura, de volta ao lugar deles sobre a prancha.
   *
   * Nenhum dos dois guarda coordenada — eles penduram na parede, e a parede é
   * reconstruída do contorno. É o que faz a planta mostrar também o que foi digitado no
   * cartão, por quem nunca abriu a prancha.
   */
  const vaosColocados = useMemo(
    () => placeOpenings(draft.openings, paredesColocadas),
    [draft.openings, paredesColocadas],
  );
  const pecasColocadas = useMemo(
    () => placeElements(draft.elements, draft.elementSegments, paredesColocadas),
    [draft.elements, draft.elementSegments, paredesColocadas],
  );

  /** A largura digitada da viga em polígono, em metros — zero enquanto não é um número. */
  const larguraDigitadaDaViga = Number(larguraDaViga.replace(',', '.')) || 0;

  /**
   * O que o traço diz sobre a peça — em metros, já com os trechos.
   *
   * No retângulo são os dois cantos clicados; no polígono, o contorno fechado, e as paredes
   * em que ele cai saem do retângulo que o envolve.
   */
  const pecaLida = useMemo(() => {
    if (escala === undefined) return undefined;
    if (pecaLivre.formato === 'POLIGONO') {
      if (pecaLivre.contorno === undefined) return undefined;
      return lerContorno(pecaKind, pecaLivre.contorno, larguraDigitadaDaViga, paredesColocadas);
    }
    const [a, b] = cantos;
    if (a === undefined || b === undefined) return undefined;
    return traceStructure(
      pecaKind,
      pixelToMetres(a, escala),
      pixelToMetres(b, escala),
      paredesColocadas,
    );
  }, [
    cantos,
    escala,
    pecaKind,
    paredesColocadas,
    pecaLivre.formato,
    pecaLivre.contorno,
    larguraDigitadaDaViga,
  ]);

  /**
   * O contorno que a peça guarda, em metros — ausente é a peça posta pelos trechos.
   *
   * O polígono sempre leva o contorno. O retângulo só leva quando **não encostou em parede
   * nenhuma**: aí o contorno é o que diz onde a peça está. Sobre parede, ele continua como
   * sempre foi, sem contorno, e o pilar de canto não muda de comportamento.
   */
  const contornoDaPeca = useMemo((): readonly Point[] | undefined => {
    if (pecaLida === undefined || escala === undefined) return undefined;
    if (pecaLivre.formato === 'POLIGONO') return pecaLivre.contorno;
    const [a, b] = cantos;
    if (a === undefined || b === undefined || pecaLida.segments.length > 0) return undefined;
    return contornoDoRetangulo(pixelToMetres(a, escala), pixelToMetres(b, escala));
  }, [pecaLida, escala, pecaLivre.formato, pecaLivre.contorno, cantos]);

  /** A altura do pilar (ou a da seção da viga) digitada, em metros — `NaN` se não é número. */
  const medidaDigitadaDaPeca = Number(medidaDaPeca.replace(',', '.'));

  /** O que impede cadastrar a peça de agora — o painel diz, e o botão espera. */
  const impedimentoDaPeca = useMemo((): string | undefined => {
    if (pecaLida === undefined) return undefined;
    // Vazio, zero, negativo e texto: a recusa aparece no painel e o botão espera. Antes o
    // botão ficava aceso e o clique não fazia nada, sem aviso.
    if (!(medidaDigitadaDaPeca > 0)) {
      return pecaKind === 'PILAR'
        ? 'Informe a altura do pilar — a planta é vista de cima e não a mostra.'
        : 'Informe a altura da seção da viga — é ela que come a alvenaria.';
    }
    if (pecaLivre.formato === 'POLIGONO' && pecaKind === 'VIGA' && !(larguraDigitadaDaViga > 0)) {
      return 'Informe a largura da viga — é ela que define as pontas do contorno.';
    }
    if (contornoDaPeca === undefined) return undefined;
    const conferido = checkElementOutline(pecaKind, contornoDaPeca, pecaLida.sectionWidth);
    return conferido.ok ? undefined : conferido.error.message;
  }, [
    pecaLida,
    pecaLivre.formato,
    pecaKind,
    larguraDigitadaDaViga,
    contornoDaPeca,
    medidaDigitadaDaPeca,
  ]);

  /** O pilar de 99 m pede conferência: o aviso aparece e o cadastro vale no segundo clique. */
  const confirmaAlturaDoPilar = useConfirmacaoDeValorGrande(
    pecaKind === 'PILAR' ? medidaDigitadaDaPeca : undefined,
    ALTURA_DO_PILAR_MAXIMA,
  );

  function cadastrarPeca(): void {
    if (pecaLida === undefined || impedimentoDaPeca !== undefined) return;
    const digitada = medidaDigitadaDaPeca;
    if (!(digitada > 0)) return;
    if (!confirmaAlturaDoPilar.confirmar()) return;

    dispatch({
      type: 'ADD_TRACED_ELEMENT',
      kind: pecaKind,
      sectionWidth: pecaLida.sectionWidth,
      // No pilar a profundidade sai do traço e a altura é digitada; na viga é o contrário.
      sectionDepth: pecaKind === 'PILAR' ? (pecaLida.sectionDepth ?? 0) : digitada,
      length: pecaKind === 'PILAR' ? digitada : (pecaLida.run ?? 0),
      ...(contornoDaPeca === undefined ? {} : { outline: contornoDaPeca }),
      segments: pecaLida.segments,
    });
    setCantos([]);
    setArrasto(undefined);
    pecaLivre.refazer();
    confirmaAlturaDoPilar.limpar();
    setErro(undefined);
  }

  /**
   * O shaft que os dois cantos desenham, em metros — e o ambiente em que ele caiu.
   *
   * O ambiente é o que contém o centro do retângulo (`roomOfShaft`), e aparece no painel
   * **antes** do cadastro: é dele que o buraco vai descontar, e o shaft traçado em cima da
   * divisa precisa dizer para qual lado caiu enquanto ainda dá para arrastar de novo.
   */
  const shaftLido = useMemo(() => {
    const [a, b] = cantos;
    if (modo !== 'SHAFT' || escala === undefined || a === undefined || b === undefined) {
      return undefined;
    }
    const cantosEmMetros = { from: pixelToMetres(a, escala), to: pixelToMetres(b, escala) };
    return { ...cantosEmMetros, room: roomOfShaft(cantosEmMetros, draft.rooms) };
  }, [cantos, escala, modo, draft.rooms]);

  function cadastrarShaft(): void {
    if (shaftLido === undefined) return;
    if (shaftLido.room === undefined) {
      setErro('Trace o shaft dentro de um ambiente — é do piso dele que o buraco desconta.');
      return;
    }
    const quantos = Number(shaftsIguais);
    if (!Number.isInteger(quantos) || quantos < 1) {
      setErro('Informe quantos shafts iguais — um número inteiro de 1 para cima.');
      return;
    }

    dispatch({
      type: 'ADD_SHAFT',
      roomId: shaftLido.room.id,
      from: shaftLido.from,
      to: shaftLido.to,
      count: quantos,
    });
    // O ambiente abre no painel, com o shaft novo na lista dele e o piso já descontado.
    setSelecionado(shaftLido.room.id);
    setCantos([]);
    setArrasto(undefined);
    setErro(undefined);
  }

  /**
   * A estaca (Fase 27): só na área comum do desenho de Fundação ou de Contenção (RN-361).
   *
   * O clique é o centro; o círculo sai no diâmetro do tipo. O estado todo mora no hook — a
   * prancha só repassa os eventos do desenho e põe o painel ao lado.
   */
  const daEstaca = estacaCabeAqui(draft);
  const estacas = useEstacasNaPrancha({
    draft,
    pileTypes: workspace.pileTypes ?? [],
    catalog: workspace.catalog,
    escala,
    zoom,
    ativo: modo === 'ESTACA',
    dispatch,
    selecionar: (id) => setPecaSelecionada(id === undefined ? undefined : { tipo: 'ESTACA', id }),
  });
  const estacaSelecionada =
    pecaSelecionada?.tipo === 'ESTACA'
      ? (draft.pieces ?? []).find((peca) => peca.id === pecaSelecionada.id)
      : undefined;

  /**
   * O bloco e o baldrame (Fase 27, etapa 2): da mesma área comum que a estaca (RN-361).
   *
   * Bloco por dois cantos ou polígono, baldrame de eixo a eixo com encaixe no centro do
   * bloco; o estado todo mora no hook, e a prancha só repassa os eventos do desenho.
   */
  const fundacao = useFundacaoNaPrancha({
    draft,
    escala,
    zoom,
    ferramenta: modo === 'BLOCO' || modo === 'BALDRAME' ? modo : undefined,
    parado: modo === 'PARADO' && movendo === undefined,
    dispatch,
    selecionar: setPecaSelecionada,
  });
  const blocoSelecionado =
    pecaSelecionada?.tipo === 'BLOCO'
      ? fundacao.blocos.find((bloco) => bloco.id === pecaSelecionada.id)
      : undefined;
  const baldrameSelecionado =
    pecaSelecionada?.tipo === 'BALDRAME'
      ? fundacao.baldrames.find((baldrame) => baldrame.id === pecaSelecionada.id)
      : undefined;
  const erroDoDesenho = draft.lastError ?? workspace.lastError;
  /** O nome da parede na lista do "Aplicar o P20 a outros pilares" (Fase 42, RN-562). */
  const nomeDaParedeDaMesa = useCallback(
    (wallId: WallId) => wallShortLabel(draft, wallId),
    [draft],
  );

  /** Liga a ferramenta do bloco ou do baldrame — ou a desliga, se já era ela. */
  function escolherFerramentaDaFundacao(ferramenta: 'BLOCO' | 'BALDRAME'): void {
    if (escala === undefined) {
      pedirEscala();
      return;
    }
    setModo(modo === ferramenta ? 'PARADO' : ferramenta);
    setCantos([]);
    setArrasto(undefined);
    setTracado([]);
    setDelimitando(undefined);
    setErro(undefined);
  }

  /**
   * O muro e o pilar de contenção (Fase 27, etapa 4): da mesma área comum que a estaca
   * (RN-361). O muro é o eixo com altura e espessura; o pilar, um clique no centro, que se
   * encaixa no muro quando cai sobre ele.
   */
  const contencao = useContencaoNaPrancha({
    draft,
    escala,
    zoom,
    ferramenta: modo === 'MURO' || modo === 'PILAR_CONTENCAO' ? modo : undefined,
    parado: modo === 'PARADO' && movendo === undefined,
    dispatch,
    selecionar: setPecaSelecionada,
  });
  const muroSelecionado =
    pecaSelecionada?.tipo === 'MURO'
      ? contencao.muros.find((muro) => muro.id === pecaSelecionada.id)
      : undefined;
  const pilarSelecionado =
    pecaSelecionada?.tipo === 'PILAR_CONTENCAO'
      ? contencao.pilares.find((pilar) => pilar.id === pecaSelecionada.id)
      : undefined;

  /** Liga a ferramenta do muro ou do pilar de contenção — ou a desliga, se já era ela. */
  function escolherFerramentaDaContencao(ferramenta: 'MURO' | 'PILAR_CONTENCAO'): void {
    if (escala === undefined) {
      pedirEscala();
      return;
    }
    setModo(modo === ferramenta ? 'PARADO' : ferramenta);
    setCantos([]);
    setArrasto(undefined);
    setTracado([]);
    setDelimitando(undefined);
    setErro(undefined);
  }

  /**
   * O radier e a laje, com as regiões de espessura diferente (Fase 27, etapa 3, D7).
   *
   * O radier é da mesma área comum que a estaca (Fundação e Contenção); a laje é da área
   * comum de **qualquer** pavimento — é a laje do andar (RN-361, F3). A região nasce na peça
   * selecionada, com o botão "Região" do painel dela.
   */
  const daRadier = radierCabeAqui(draft);
  const daLaje = lajeCabeAqui(draft);
  const lajeEscolhida: PecaDaLaje | undefined =
    pecaSelecionada?.tipo === 'LAJE' || pecaSelecionada?.tipo === 'REGIAO'
      ? pecaSelecionada
      : undefined;
  const laje = useLajeNaPrancha({
    draft,
    escala,
    zoom,
    ferramenta: modo === 'RADIER' || modo === 'LAJE' || modo === 'REGIAO' ? modo : undefined,
    parado: modo === 'PARADO' && movendo === undefined,
    selecionada: lajeEscolhida,
    dispatch,
    selecionar: setPecaSelecionada,
  });
  const lajeSelecionada =
    lajeEscolhida === undefined
      ? undefined
      : laje.pecas.find((peca) => peca.id === lajeEscolhida.id);

  /** Liga a ferramenta do radier ou da laje — ou a desliga, se já era ela. */
  function escolherFerramentaDaLaje(ferramenta: 'RADIER' | 'LAJE'): void {
    if (escala === undefined) {
      pedirEscala();
      return;
    }
    setModo(modo === ferramenta ? 'PARADO' : ferramenta);
    setCantos([]);
    setArrasto(undefined);
    setTracado([]);
    setDelimitando(undefined);
    setErro(undefined);
  }

  /** Liga o traço da região dentro da peça selecionada — ou o desliga, se já estava ligado. */
  function desenharRegiao(): void {
    if (escala === undefined) {
      pedirEscala();
      return;
    }
    setModo(modo === 'REGIAO' ? 'PARADO' : 'REGIAO');
    setCantos([]);
    setArrasto(undefined);
    setTracado([]);
    setDelimitando(undefined);
    setErro(undefined);
  }

  /**
   * A escada de concreto, com os lances e os patamares (Fase 27, etapa 5, D10): da área comum
   * de **qualquer** pavimento, como a laje (F3). O lance e o patamar nascem na escada
   * selecionada, com os botões do painel dela.
   */
  const daEscada = escadaCabeAqui(draft);
  const escadaEscolhida: PecaDaEscada | undefined =
    pecaSelecionada?.tipo === 'ESCADA' ||
    pecaSelecionada?.tipo === 'LANCE' ||
    pecaSelecionada?.tipo === 'PATAMAR'
      ? pecaSelecionada
      : undefined;
  const escada = useEscadaNaPrancha({
    draft,
    escala,
    zoom,
    ferramenta: modo === 'ESCADA' || modo === 'LANCE' || modo === 'PATAMAR' ? modo : undefined,
    parado: modo === 'PARADO' && movendo === undefined,
    selecionada: escadaEscolhida,
    dispatch,
    selecionar: setPecaSelecionada,
  });
  const escadaSelecionada =
    escadaEscolhida === undefined
      ? undefined
      : escada.escadas.find((candidata) => candidata.id === escadaEscolhida.id);
  const modoDaEscada = modo === 'ESCADA' || modo === 'LANCE' || modo === 'PATAMAR';

  /** Liga a ferramenta da escada — ou a desliga, se já era ela. */
  function escolherFerramentaDaEscada(ferramenta: 'ESCADA'): void {
    if (escala === undefined) {
      pedirEscala();
      return;
    }
    setModo(modo === ferramenta ? 'PARADO' : ferramenta);
    setCantos([]);
    setArrasto(undefined);
    setTracado([]);
    setDelimitando(undefined);
    setErro(undefined);
  }

  /** Liga o traço do lance ou do patamar na escada selecionada — ou o desliga, se já estava. */
  function desenharParteDaEscada(ferramenta: 'LANCE' | 'PATAMAR'): void {
    if (escala === undefined) {
      pedirEscala();
      return;
    }
    setModo(modo === ferramenta ? 'PARADO' : ferramenta);
    setCantos([]);
    setArrasto(undefined);
    setTracado([]);
    setDelimitando(undefined);
    setErro(undefined);
  }

  /**
   * O desenho só responde a clique com a prancha **parada**.
   *
   * No meio de um traço, o clique é um canto do contorno — e o canto que mais se clica é
   * justamente o do vizinho, porque é ali que a divisa fecha. Com a vizinha atendendo o
   * clique, cada canto encostado nela trocava a tipologia na mesa, e a troca descarta o
   * traço pela metade: o apto ao lado não havia como ser desenhado. O mesmo vale para o
   * pilar arrastado, para a calibragem e para a parede empurrada.
   */
  const desenhoClicavel = modo === 'PARADO' && movendo === undefined;

  const emMetros = useMemo(
    (): readonly Point[] =>
      escala === undefined ? [] : tracado.map((pixel) => pixelToMetres(pixel, escala)),
    [tracado, escala],
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>Planta — {nomeDaTipologia(draft)}</DialogTitle>
        <DialogDescription>
          Calibre numa cota conhecida e trace o contorno de cada ambiente — e, depois deles, o pilar
          e a viga. As medidas aparecem à direita enquanto você clica. O andar inteiro sai da mesma
          folha: troque a tipologia aqui em cima e siga desenhando. Para corrigir o que já está
          traçado, <strong>dê dois cliques numa parede</strong> e empurre-a de lado.
        </DialogDescription>
      </DialogHeader>

      <TipologiasDaPrancha workspace={workspace} draft={draft} dispatch={dispatch} />

      <div className="flex flex-wrap items-center gap-2 border-b border-(--color-border-subtle) pb-3">
        <input
          ref={entrada}
          type="file"
          accept="application/pdf,image/*"
          className="hidden"
          onChange={(evento) => {
            const arquivo = evento.target.files?.[0];
            if (arquivo !== undefined) void carregar(arquivo);
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={daObra?.enviando === true}
          onClick={() => {
            // A que está lá tem dono: trocar pergunta antes de abrir o seletor (RN-246).
            if (anexada !== undefined) setConfirmandoTroca(true);
            else entrada.current?.click();
          }}
        >
          <FileUp className="size-4" />{' '}
          {daObra?.enviando === true ? 'Enviando…' : temPrancha ? 'Trocar prancha' : 'Carregar'}
        </Button>
        {anexada !== undefined && <QuemAnexou anexada={anexada} />}

        {plan !== undefined && (
          <>
            <Button
              size="sm"
              variant={modo === 'CALIBRANDO' ? 'primary' : 'ghost'}
              onClick={() => {
                setModo(modo === 'CALIBRANDO' ? 'PARADO' : 'CALIBRANDO');
                setCalibragem([]);
              }}
            >
              <Ruler className="size-4" /> Calibrar escala
            </Button>
            <Button
              size="sm"
              variant={modo === 'TRACANDO' && delimitando === undefined ? 'primary' : 'ghost'}
              onClick={() => {
                if (escala === undefined) {
                  pedirEscala();
                  return;
                }
                setModo(modo === 'TRACANDO' ? 'PARADO' : 'TRACANDO');
                setDelimitando(undefined);
                setTracado([]);
              }}
            >
              <Crosshair className="size-4" /> Novo ambiente
            </Button>
            <Button
              size="sm"
              variant={modo === 'ESTRUTURA' ? 'primary' : 'ghost'}
              onClick={() => {
                if (escala === undefined) {
                  pedirEscala();
                  return;
                }
                setModo(modo === 'ESTRUTURA' ? 'PARADO' : 'ESTRUTURA');
                setCantos([]);
                setArrasto(undefined);
                setTracado([]);
                setDelimitando(undefined);
              }}
            >
              <Columns3 className="size-4" /> Pilar e viga
            </Button>
            <Button
              size="sm"
              variant={modo === 'VAO' ? 'primary' : 'ghost'}
              onClick={() => {
                if (escala === undefined) {
                  pedirEscala();
                  return;
                }
                setModo(modo === 'VAO' ? 'PARADO' : 'VAO');
                setTracado([]);
                setCantos([]);
                setArrasto(undefined);
                setDelimitando(undefined);
                setErro(undefined);
              }}
            >
              <DoorOpen className="size-4" /> Vão
            </Button>
            <Button
              size="sm"
              variant={modo === 'SHAFT' ? 'primary' : 'ghost'}
              onClick={() => {
                if (escala === undefined) {
                  pedirEscala();
                  return;
                }
                setModo(modo === 'SHAFT' ? 'PARADO' : 'SHAFT');
                setCantos([]);
                setArrasto(undefined);
                setTracado([]);
                setDelimitando(undefined);
                setErro(undefined);
              }}
            >
              <SquareX className="size-4" /> Shaft
            </Button>
            {/* A estaca é da Fundação e da Contenção: nas outras tipologias o botão nem existe. */}
            {daEstaca && (
              <Button
                size="sm"
                variant={modo === 'ESTACA' ? 'primary' : 'ghost'}
                onClick={() => {
                  if (escala === undefined) {
                    pedirEscala();
                    return;
                  }
                  setModo(modo === 'ESTACA' ? 'PARADO' : 'ESTACA');
                  setCantos([]);
                  setArrasto(undefined);
                  setTracado([]);
                  setDelimitando(undefined);
                  setErro(undefined);
                }}
              >
                <CircleDot className="size-4" /> Estaca
              </Button>
            )}
            {/* O bloco e o baldrame moram onde a estaca mora. */}
            {daEstaca && (
              <BotoesDaFundacao
                ferramenta={fundacao.ferramenta}
                onEscolher={escolherFerramentaDaFundacao}
              />
            )}
            {/* O arranque só existe pela planilha (Fase 28): o botão aparece quando ela os traz. */}
            {daEstaca &&
              (pecasDaPlanilha?.pecas ?? []).some((peca) => peca.kind === 'ARRANQUE') && (
                <Button
                  size="sm"
                  variant={modo === 'ARRANQUE' ? 'primary' : 'ghost'}
                  onClick={() => {
                    if (escala === undefined) {
                      pedirEscala();
                      return;
                    }
                    setModo(modo === 'ARRANQUE' ? 'PARADO' : 'ARRANQUE');
                    setPosicionandoPeca(undefined);
                    setCantos([]);
                    setArrasto(undefined);
                    setTracado([]);
                    setDelimitando(undefined);
                    setErro(undefined);
                  }}
                >
                  <Diamond className="size-4" /> Arranque
                </Button>
              )}
            {/* O muro e o pilar de contenção moram onde a estaca mora. */}
            {daEstaca && (
              <BotoesDaContencao
                ferramenta={contencao.ferramenta}
                onEscolher={escolherFerramentaDaContencao}
              />
            )}
            {/* O radier mora onde a estaca mora; a laje, na área comum de qualquer pavimento. */}
            {daLaje && (
              <BotoesDaLaje
                ferramenta={laje.ferramenta === 'REGIAO' ? undefined : laje.ferramenta}
                comRadier={daRadier}
                onEscolher={escolherFerramentaDaLaje}
              />
            )}
            {/* A escada de concreto, como a laje, é da área comum de qualquer pavimento. */}
            {daEscada && (
              <BotoesDaEscada
                ferramenta={escada.ferramenta === 'ESCADA' ? 'ESCADA' : undefined}
                onEscolher={escolherFerramentaDaEscada}
              />
            )}
            <Button
              size="sm"
              variant={mostrarNomes ? 'secondary' : 'ghost'}
              aria-pressed={mostrarNomes}
              onClick={() => setMostrarNomes((atual) => !atual)}
            >
              <Tags className="size-4" /> Nomes das paredes
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmandoRemocao(true)}>
              <Trash2 className="size-4" /> Remover prancha
            </Button>
            {escala === undefined ? (
              <Badge tone="warn">sem escala</Badge>
            ) : (
              <Badge tone="ok">1 px = {escala.toFixed(4)} m</Badge>
            )}

            <span className="ml-auto flex items-center gap-1">
              <Button
                size="icon"
                variant="ghost"
                aria-label="Diminuir zoom"
                disabled={zoom <= ZOOM_MIN}
                onClick={() => aplicarZoom(zoom / 1.25)}
              >
                <ZoomOut className="size-4" />
              </Button>
              <span
                className="w-14 text-center text-xs tabular-nums text-(--color-content-muted)"
                aria-live="polite"
              >
                {Math.round(zoom * 100)}%
              </span>
              <Button
                size="icon"
                variant="ghost"
                aria-label="Aumentar zoom"
                disabled={zoom >= ZOOM_MAX}
                onClick={() => aplicarZoom(zoom * 1.25)}
              >
                <ZoomIn className="size-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={ajustar}>
                Ajustar
              </Button>
              <span className="hidden text-xs text-(--color-content-muted) lg:inline">
                ou ctrl + roda
              </span>
            </span>
          </>
        )}
      </div>

      {erro !== undefined && (
        <p className="mt-3 rounded-md border border-(--color-state-danger) px-3 py-2 text-sm">
          {erro}
        </p>
      )}
      {aviso !== undefined && (
        <p className="mt-3 rounded-md border border-(--color-border-subtle) px-3 py-2 text-sm">
          {aviso}
        </p>
      )}

      <div className="mt-3 grid min-h-0 flex-1 gap-4 md:grid-cols-[minmax(0,1fr)_22rem]">
        {/* ── A prancha ────────────────────────────────────────────────────── */}
        <div className="flex min-h-0 flex-col gap-2">
          <div
            ref={prancha}
            className="min-h-0 flex-1 overflow-auto rounded-md border border-(--color-border-subtle)"
          >
            {plan === undefined ? (
              <p className="p-8 text-center text-sm text-(--color-content-muted)">
                {daObra?.falha ??
                  (anexada !== undefined || daObra?.abrindo === true
                    ? 'Trazendo a prancha deste pavimento…'
                    : 'Carregue o PDF da prancha ou uma imagem dela para começar.')}
              </p>
            ) : (
              /*
              A ampliação é `transform` na camada de fora, e não tamanho novo na de dentro:
              imagem e `svg` continuam medindo o pixel da prancha, que é a única unidade em
              que o traçado pode ser guardado. O invólucro reserva o espaço ampliado para a
              barra de rolagem saber o tamanho real.
            */
              <div style={{ width: plan.width * zoom, height: plan.height * zoom }}>
                <div
                  className="relative origin-top-left"
                  style={{
                    width: plan.width,
                    height: plan.height,
                    transform: `scale(${String(zoom)})`,
                  }}
                >
                  <img
                    src={plan.image}
                    alt="Planta do pavimento"
                    width={plan.width}
                    height={plan.height}
                  />
                  <svg
                    className={
                      // `select-none`: o duplo clique que agarra a parede é, para o
                      // navegador, um pedido de seleção de texto — e a prancha inteira
                      // ficava azul no meio do gesto.
                      movendo !== undefined
                        ? 'absolute inset-0 cursor-move select-none'
                        : modo === 'PARADO'
                          ? 'absolute inset-0 select-none'
                          : 'absolute inset-0 cursor-crosshair select-none'
                    }
                    width={plan.width}
                    height={plan.height}
                    onClick={clique}
                    onDoubleClick={pegarParede}
                    onMouseDown={apertar}
                    onMouseMove={acompanhar}
                    onMouseUp={soltar}
                    /*
                      Sair da prancha com o botão apertado desiste do arrasto, em vez de
                      deixar a peça grudada no mouse até alguém clicar de novo.
                    */
                    onMouseLeave={() => {
                      setArrasto(undefined);
                      estacas.cancelarArrasto();
                      fundacao.cancelarArrasto();
                      laje.cancelarArrasto();
                      contencao.cancelarArrasto();
                      escada.cancelarArrasto();
                      pecaLivre.cancelarArrasto();
                    }}
                  >
                    {escala !== undefined && (
                      <>
                        {/*
                          As vizinhas vêm **antes**, e por baixo: elas são referência, não
                          trabalho. Clicar numa delas é o caminho curto para seguir o andar
                          — a tipologia troca e a prancha não se move.
                        */}
                        {/*
                          O radier e a laje são o chão de tudo: por baixo das vizinhas, do
                          ambiente e da parede. A laje cobre o andar, e por cima do ambiente
                          ela tomaria o clique dele.
                        */}
                        <LajesDesenhadas
                          pecas={laje.pecas}
                          escala={escala}
                          zoom={zoom}
                          selecionada={lajeEscolhida}
                          arrastando={laje.arrastando}
                          clicaveis={laje.clicaveis}
                          onClicarLaje={laje.clicarNaLaje}
                          onClicarRegiao={laje.clicarNaRegiao}
                        />
                        {irmas.map((typology) => (
                          <TipologiaAoFundo
                            key={typology.unitTypeId}
                            typology={typology}
                            escala={escala}
                            zoom={zoom}
                            onSelecionar={
                              desenhoClicavel
                                ? () =>
                                    dispatch({
                                      type: 'SELECT_TYPOLOGY',
                                      unitTypeId: typology.unitTypeId,
                                    })
                                : undefined
                            }
                          />
                        ))}
                        {draft.rooms.map((room) =>
                          room.outline === undefined ? null : (
                            <PoligonoPronto
                              key={room.id}
                              room={room}
                              escala={escala}
                              zoom={zoom}
                              selecionado={room.id === selecionado}
                              onSelecionar={
                                desenhoClicavel ? () => setSelecionado(room.id) : undefined
                              }
                            />
                          ),
                        )}
                        {/*
                          O shaft é o chão que falta: por cima do ambiente, que ele fura, e
                          por baixo da parede, que continua inteira em volta dele.
                        */}
                        <ShaftsDesenhados
                          shafts={draft.shafts ?? []}
                          escala={escala}
                          zoom={zoom}
                          selecionado={
                            pecaSelecionada?.tipo === 'SHAFT' ? pecaSelecionada.id : undefined
                          }
                          onSelecionar={
                            desenhoClicavel
                              ? (id) => setPecaSelecionada({ tipo: 'SHAFT', id })
                              : undefined
                          }
                        />
                        {/*
                          A estaca é círculo no diâmetro do tipo, com o nome dentro. Mora na
                          área comum da Fundação, onde não há parede nem ambiente por perto —
                          a ordem de pintura entre eles não decide nada.
                        */}
                        {/*
                          O bloco e o baldrame por baixo da estaca, que se apoia neles: a
                          estaca fica por cima, e continua clicável onde o bloco a cobre.
                        */}
                        <FundacaoDesenhada
                          blocos={fundacao.blocos}
                          baldrames={fundacao.baldrames}
                          escala={escala}
                          zoom={zoom}
                          selecionada={
                            pecaSelecionada?.tipo === 'BLOCO' ||
                            pecaSelecionada?.tipo === 'BALDRAME'
                              ? pecaSelecionada
                              : undefined
                          }
                          arrastando={fundacao.arrastando}
                          blocosClicaveis={fundacao.blocosClicaveis}
                          baldramesClicaveis={fundacao.baldramesClicaveis}
                          onClicarBloco={fundacao.clicarNoBloco}
                          onClicarBaldrame={fundacao.clicarNoBaldrame}
                        />
                        <ContencaoDesenhada
                          muros={contencao.muros}
                          pilares={contencao.pilares}
                          escala={escala}
                          zoom={zoom}
                          selecionada={
                            pecaSelecionada?.tipo === 'MURO' ||
                            pecaSelecionada?.tipo === 'PILAR_CONTENCAO'
                              ? pecaSelecionada
                              : undefined
                          }
                          arrastando={contencao.arrastando}
                          clicaveis={contencao.clicaveis}
                          onClicarMuro={contencao.clicarNoMuro}
                          onClicarPilar={contencao.clicarNoPilar}
                        />
                        {(daEstaca || (draft.pieces ?? []).length > 0) && (
                          <EstacasDesenhadas
                            pecas={draft.pieces ?? []}
                            tipos={workspace.pileTypes ?? []}
                            marcas={draft.pieceMarks ?? []}
                            servicoMarcado={
                              modo === 'ESTACA' && estacas.acao === 'MARCAR'
                                ? estacas.servico?.key
                                : undefined
                            }
                            escala={escala}
                            zoom={zoom}
                            selecionada={
                              pecaSelecionada?.tipo === 'ESTACA' ? pecaSelecionada.id : undefined
                            }
                            arrastando={estacas.arrastando}
                            novo={
                              modo === 'ESTACA' &&
                              estacas.acao === 'NOVA' &&
                              estacas.centro !== undefined
                                ? {
                                    centro: estacas.centro,
                                    tipo: (workspace.pileTypes ?? []).find(
                                      (tipo) => tipo.id === estacas.tipoDaNova,
                                    ),
                                  }
                                : undefined
                            }
                            clicavel={
                              (modo === 'PARADO' || modo === 'ESTACA') && movendo === undefined
                            }
                            onClicar={estacas.clicarNaEstaca}
                          />
                        )}
                        <FundacaoEmTraco
                          fundacao={fundacao}
                          blocos={fundacao.blocos}
                          escala={escala}
                          zoom={zoom}
                        />
                        {pecasDaPlanilha !== undefined && (
                          <PecasDaPlanilhaDesenhadas
                            pecas={pecasDaPlanilha.pecas}
                            escala={escala}
                            zoom={zoom}
                            destaque={tipoDaFerramenta(modo)}
                            posicionando={pecaNaMao}
                            clicaveis={pecaNaMao === undefined && movendo === undefined}
                          />
                        )}
                        <ContencaoEmTraco contencao={contencao} escala={escala} zoom={zoom} />
                        <LajeEmTraco laje={laje} zoom={zoom} />
                        <EscadasDesenhadas
                          escadas={escada.escadas}
                          escala={escala}
                          zoom={zoom}
                          selecionada={escadaEscolhida}
                          arrastando={escada.arrastando}
                          clicavel={escada.clicavel}
                          onClicar={escada.clicarNaEscada}
                        />
                        <EscadaEmTraco escada={escada} zoom={zoom} />
                        {/*
                        A ordem de pintura é a da obra: a parede por cima do ambiente, o
                        vão por cima da parede — ele é o buraco nela —, e a estrutura por
                        último, que é o que a laje esconde e ninguém pode perder de vista.
                      */}
                        <ParedesDesenhadas
                          paredes={paredesColocadas}
                          porId={paredePorId}
                          nomeDaParede={(wallId) => wallShortLabel(draft, wallId)}
                          escala={escala}
                          zoom={zoom}
                          mostrarNomes={mostrarNomes}
                          selecionada={paredeSelecionada}
                          {...(desenhoClicavel ? { onSelecionar: selecionarParede } : {})}
                        />
                        <GuardaCorposDesenhados
                          rooms={draft.rooms}
                          paredes={paredesColocadas}
                          porId={paredePorId}
                          escala={escala}
                          zoom={zoom}
                        />
                        <VaosDesenhados
                          vaos={vaosColocados}
                          escala={escala}
                          zoom={zoom}
                          selecionado={
                            pecaSelecionada?.tipo === 'VAO' ? pecaSelecionada.id : undefined
                          }
                          onSelecionar={
                            desenhoClicavel
                              ? (id) => setPecaSelecionada({ tipo: 'VAO', id })
                              : undefined
                          }
                        />
                        <EstruturaDesenhada
                          pecas={pecasColocadas}
                          escala={escala}
                          zoom={zoom}
                          selecionado={
                            pecaSelecionada?.tipo === 'PECA' ? pecaSelecionada.id : undefined
                          }
                          arrastando={
                            pecaLivre.arrastando?.moveu === true
                              ? {
                                  id: pecaLivre.arrastando.id,
                                  dx: pecaLivre.arrastando.atual.x - pecaLivre.arrastando.origem.x,
                                  dy: pecaLivre.arrastando.atual.y - pecaLivre.arrastando.origem.y,
                                }
                              : undefined
                          }
                          onSelecionar={
                            desenhoClicavel
                              ? (id) => {
                                  // O clique que o fim do arrasto dispara é do arrasto.
                                  if (pecaLivre.foiCliqueDoArrasto()) return;
                                  setPecaSelecionada({ tipo: 'PECA', id });
                                }
                              : undefined
                          }
                        />
                      </>
                    )}

                    {calibragem.map((ponto, indice) => (
                      <circle
                        key={`cal-${String(indice)}`}
                        cx={ponto.x}
                        cy={ponto.y}
                        r={5 / zoom}
                        className="fill-(--color-state-warn)"
                      />
                    ))}
                    {calibragem.length === 2 && (
                      <line
                        x1={calibragem[0]!.x}
                        y1={calibragem[0]!.y}
                        x2={calibragem[1]!.x}
                        y2={calibragem[1]!.y}
                        className="stroke-(--color-state-warn)"
                        strokeWidth={2 / zoom}
                      />
                    )}

                    {cantos.length === 2 && (
                      <rect
                        x={Math.min(cantos[0]!.x, cantos[1]!.x)}
                        y={Math.min(cantos[0]!.y, cantos[1]!.y)}
                        width={Math.abs(cantos[1]!.x - cantos[0]!.x)}
                        height={Math.abs(cantos[1]!.y - cantos[0]!.y)}
                        className="fill-(--color-brand)/25 stroke-(--color-brand)"
                        strokeWidth={2 / zoom}
                      />
                    )}
                    {modo === 'ESTRUTURA' && (
                      <PecaEmTraco peca={pecaLivre} cor={CORES[pecaKind]} zoom={zoom} />
                    )}
                    {cantos.map((canto, indice) => (
                      <circle
                        key={`c-${String(indice)}`}
                        cx={canto.x}
                        cy={canto.y}
                        r={5 / zoom}
                        className="fill-(--color-brand)"
                      />
                    ))}

                    {/*
                      O traço em andamento, lado a lado: o que está no esquadro sai reto, o
                      que não está sai **serrilhado**. O aviso vale enquanto ainda dá para
                      desfazer o ponto — depois de fechado, o erro já está no orçamento.
                    */}
                    {tracado.map((ponto, indice) => {
                      const proximo =
                        tracado[indice + 1] ?? (indice === tracado.length - 1 ? cursor : undefined);
                      if (proximo === undefined) return null;
                      return (
                        <TracoEmAndamento
                          key={`s-${String(indice)}`}
                          de={ponto}
                          ate={proximo}
                          zoom={zoom}
                          escala={escala ?? 1}
                          vivo={indice === tracado.length - 1}
                        />
                      );
                    })}
                    {tracado.map((ponto, indice) => (
                      <circle
                        key={`t-${String(indice)}`}
                        cx={ponto.x}
                        cy={ponto.y}
                        r={(indice === 0 ? 6 : 4) / zoom}
                        className="fill-(--color-brand)"
                      />
                    ))}

                    {/* A prévia do empurrão: onde a parede vai parar quando o clique vier. */}
                    {escala !== undefined && movendo !== undefined && (
                      <ParedeEmpurrada
                        alvo={movendo.alvo}
                        distancia={movendo.distancia}
                        empurrados={empurrados}
                        areaDoAlvo={areaEmpurrada}
                        escala={escala}
                        zoom={zoom}
                      />
                    )}
                  </svg>
                </div>
              </div>
            )}
          </div>
          {plan !== undefined && escala !== undefined && (
            <Legenda
              comVizinhas={irmas.length > 0}
              comEstaca={daEstaca}
              comRadier={daRadier}
              comLaje={daLaje}
              comEscada={daEscada}
              comPecasDaPlanilha={(pecasDaPlanilha?.pecas ?? []).some(
                (peca) => peca.position !== undefined,
              )}
            />
          )}
        </div>

        {/* ── O que a prancha produz ───────────────────────────────────────── */}
        <div className="flex min-h-0 flex-col gap-3 overflow-auto">
          {movendo !== undefined && (
            <section className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3">
              <h3 className="text-sm font-semibold">Empurrando a parede</h3>
              <p className="text-xs text-(--color-content-muted)">
                A parede anda na perpendicular dela. Clique para gravar, <kbd>Esc</kbd> para
                desistir. A divisa leva o vizinho junto.
              </p>
              <dl className="flex flex-col gap-1 text-sm">
                <Linha
                  rotulo="Deslocamento"
                  valor={`${String(Math.abs(Math.round(movendo.distancia * 100)))} cm`}
                />
                <Linha
                  rotulo="Área do ambiente"
                  valor={`${(areaEmpurrada?.depois ?? 0).toFixed(2)} m²`}
                  destaque
                />
                <Linha rotulo="Ambientes afetados" valor={String(Math.max(1, empurrados.length))} />
              </dl>
              <Button variant="ghost" size="sm" onClick={() => setMovendo(undefined)}>
                Cancelar
              </Button>
            </section>
          )}

          {modo === 'CALIBRANDO' && (
            <section className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3">
              <h3 className="text-sm font-semibold">Escala</h3>
              {escala === undefined && (
                <p className="text-xs font-medium">
                  Calibre antes de traçar: sem escala o ambiente não tem medida, e o traço não pode
                  começar.
                </p>
              )}
              <p className="text-xs text-(--color-content-muted)">
                Clique nas duas pontas de uma cota conhecida e informe a medida real. A escala sai
                daqui — nunca do &quot;1:50&quot; escrito na prancha, que o PDF pode ter distorcido
                ao exportar.
              </p>
              <div className="flex items-end gap-2">
                <Field label="Medida real (m)" htmlFor="calibragem">
                  <Input
                    id="calibragem"
                    inputMode="decimal"
                    value={medidaReal}
                    onChange={(evento) => setMedidaReal(evento.target.value)}
                    className="w-28"
                  />
                </Field>
                <Button variant="primary" disabled={calibragem.length < 2} onClick={calibrar}>
                  Aplicar
                </Button>
              </div>
              <span className="text-xs text-(--color-content-muted)">
                {calibragem.length} de 2 pontos
              </span>
            </section>
          )}

          {modo === 'ESTRUTURA' && (
            <EmEstrutura
              kind={pecaKind}
              cantos={cantos.length}
              peca={pecaLida}
              pecaLivre={pecaLivre}
              impedimento={impedimentoDaPeca}
              avisoDaMedida={confirmaAlturaDoPilar.aviso}
              medida={medidaDaPeca}
              largura={larguraDaViga}
              nomeDaParede={(wallId) => wallShortLabel(draft, wallId)}
              onKind={(escolhido) => {
                setPecaKind(escolhido);
                setMedidaDaPeca(escolhido === 'PILAR' ? '2,9' : '0,5');
              }}
              onMedida={setMedidaDaPeca}
              onLargura={setLarguraDaViga}
              onForma={(formato) => {
                pecaLivre.escolherFormato(formato);
                setCantos([]);
                setArrasto(undefined);
              }}
              onRefazer={() => {
                pecaLivre.refazer();
                setCantos([]);
                setArrasto(undefined);
              }}
              onCadastrar={cadastrarPeca}
              onCancelar={() => {
                setCantos([]);
                setArrasto(undefined);
                setModo('PARADO');
              }}
            />
          )}

          {modo === 'SHAFT' && (
            <EmShaft
              cantos={cantos.length}
              shaft={shaftLido}
              iguais={shaftsIguais}
              onIguais={setShaftsIguais}
              onCadastrar={cadastrarShaft}
              onCancelar={() => {
                setCantos([]);
                setArrasto(undefined);
                setModo('PARADO');
              }}
            />
          )}

          {modo === 'ESTACA' && (
            <EmEstaca
              estacas={estacas}
              erro={draft.lastError ?? workspace.lastError}
              tiposDaObra={
                <TiposDaObra workspace={workspace} projectId={projectId} dispatch={dispatch} />
              }
              onSair={() => setModo('PARADO')}
            />
          )}

          {estacaSelecionada !== undefined && (
            <EstacaSelecionada
              estaca={estacaSelecionada}
              tipos={workspace.pileTypes ?? []}
              marcas={draft.pieceMarks ?? []}
              catalogo={workspace.catalog}
              pecasDaPlanilha={draft.pieceQuantities}
              dispatch={dispatch}
              onFechar={() => setPecaSelecionada(undefined)}
            />
          )}

          {modo === 'BLOCO' && (
            <EmBloco fundacao={fundacao} erro={erroDoDesenho} onSair={() => setModo('PARADO')} />
          )}

          {modo === 'BALDRAME' && (
            <EmBaldrame
              fundacao={fundacao}
              blocos={fundacao.blocos}
              erro={erroDoDesenho}
              onSair={() => setModo('PARADO')}
            />
          )}

          {blocoSelecionado !== undefined && (
            <BlocoSelecionado
              key={blocoSelecionado.id}
              bloco={blocoSelecionado}
              erro={modo === 'BLOCO' ? undefined : erroDoDesenho}
              blocos={fundacao.blocos}
              tipos={workspace.pileCapTypes ?? []}
              {...(projectId === undefined ? {} : { projectId })}
              catalogo={workspace.catalog}
              pecasDaPlanilha={draft.pieceQuantities}
              media={mediaDoApartamento}
              aprovados={aprovados}
              dispatch={dispatch}
              onFechar={() => setPecaSelecionada(undefined)}
            />
          )}

          {baldrameSelecionado !== undefined && (
            <BaldrameSelecionado
              key={baldrameSelecionado.id}
              baldrame={baldrameSelecionado}
              blocos={fundacao.blocos}
              erro={modo === 'BALDRAME' ? undefined : erroDoDesenho}
              catalogo={workspace.catalog}
              pecasDaPlanilha={draft.pieceQuantities}
              media={mediaDoApartamento}
              dispatch={dispatch}
              onFechar={() => setPecaSelecionada(undefined)}
            />
          )}

          {modo === 'MURO' && (
            <EmMuro contencao={contencao} erro={erroDoDesenho} onSair={() => setModo('PARADO')} />
          )}

          {modo === 'PILAR_CONTENCAO' && (
            <EmPilarDeContencao
              contencao={contencao}
              erro={erroDoDesenho}
              onSair={() => setModo('PARADO')}
            />
          )}

          {muroSelecionado !== undefined && (
            <MuroSelecionado
              key={muroSelecionado.id}
              muro={muroSelecionado}
              pilares={contencao.pilares}
              erro={modo === 'MURO' ? undefined : erroDoDesenho}
              catalogo={workspace.catalog}
              pecasDaPlanilha={draft.pieceQuantities}
              aprovados={aprovados}
              media={mediaDoApartamento}
              dispatch={dispatch}
              onFechar={() => setPecaSelecionada(undefined)}
            />
          )}

          {pilarSelecionado !== undefined && (
            <PilarDeContencaoSelecionado
              key={pilarSelecionado.id}
              pilar={pilarSelecionado}
              muros={contencao.muros}
              erro={modo === 'PILAR_CONTENCAO' ? undefined : erroDoDesenho}
              catalogo={workspace.catalog}
              pecasDaPlanilha={draft.pieceQuantities}
              aprovados={aprovados}
              media={mediaDoApartamento}
              dispatch={dispatch}
              onFechar={() => setPecaSelecionada(undefined)}
            />
          )}

          {(modo === 'RADIER' || modo === 'LAJE') && (
            <EmLaje
              laje={laje}
              erro={erroDoDesenho}
              onSelecionar={setPecaSelecionada}
              onSair={() => setModo('PARADO')}
            />
          )}

          {modo === 'REGIAO' && (
            <EmRegiao laje={laje} erro={erroDoDesenho} onSair={() => setModo('PARADO')} />
          )}

          {lajeSelecionada !== undefined && lajeEscolhida?.tipo === 'LAJE' && (
            <LajeSelecionada
              key={lajeSelecionada.id}
              peca={lajeSelecionada}
              erro={
                modo === 'RADIER' || modo === 'LAJE' || modo === 'REGIAO'
                  ? undefined
                  : erroDoDesenho
              }
              catalogo={workspace.catalog}
              pecasDaPlanilha={draft.pieceQuantities}
              media={mediaDoApartamento}
              dispatch={dispatch}
              desenhandoRegiao={modo === 'REGIAO'}
              onDesenharRegiao={desenharRegiao}
              onSelecionarRegiao={(indice) =>
                setPecaSelecionada({ tipo: 'REGIAO', id: lajeSelecionada.id, indice })
              }
              onFechar={() => {
                setPecaSelecionada(undefined);
                if (modo === 'REGIAO') setModo('PARADO');
              }}
            />
          )}

          {lajeSelecionada !== undefined && lajeEscolhida?.tipo === 'REGIAO' && (
            <RegiaoSelecionada
              key={`${lajeSelecionada.id}-${String(lajeEscolhida.indice)}`}
              peca={lajeSelecionada}
              indice={lajeEscolhida.indice}
              erro={modo === 'REGIAO' ? undefined : erroDoDesenho}
              dispatch={dispatch}
              onFechar={() => setPecaSelecionada(undefined)}
            />
          )}

          {modo === 'ESCADA' && (
            <EmEscada
              escada={escada}
              erro={erroDoDesenho}
              onSelecionar={setPecaSelecionada}
              onSair={() => setModo('PARADO')}
            />
          )}

          {(modo === 'LANCE' || modo === 'PATAMAR') && (
            <EmParteDaEscada
              escada={escada}
              erro={erroDoDesenho}
              onSair={() => setModo('PARADO')}
            />
          )}

          {escadaSelecionada !== undefined && escadaEscolhida?.tipo === 'ESCADA' && (
            <EscadaSelecionada
              key={escadaSelecionada.id}
              escada={escadaSelecionada}
              erro={modoDaEscada ? undefined : erroDoDesenho}
              catalogo={workspace.catalog}
              pecasDaPlanilha={draft.pieceQuantities}
              aprovados={aprovados}
              media={mediaDoApartamento}
              dispatch={dispatch}
              desenhando={escada.ferramenta}
              onDesenhar={desenharParteDaEscada}
              onSelecionarParte={(parte) =>
                setPecaSelecionada({ ...parte, id: escadaSelecionada.id })
              }
              onFechar={() => {
                setPecaSelecionada(undefined);
                if (modoDaEscada) setModo('PARADO');
              }}
            />
          )}

          {escadaSelecionada !== undefined && escadaEscolhida?.tipo === 'LANCE' && (
            <LanceSelecionado
              key={`${escadaSelecionada.id}-l${String(escadaEscolhida.indice)}`}
              escada={escadaSelecionada}
              indice={escadaEscolhida.indice}
              erro={modoDaEscada ? undefined : erroDoDesenho}
              dispatch={dispatch}
              onVoltar={() => setPecaSelecionada({ tipo: 'ESCADA', id: escadaSelecionada.id })}
              onFechar={() => setPecaSelecionada(undefined)}
            />
          )}

          {escadaSelecionada !== undefined && escadaEscolhida?.tipo === 'PATAMAR' && (
            <PatamarSelecionado
              key={`${escadaSelecionada.id}-p${String(escadaEscolhida.indice)}`}
              escada={escadaSelecionada}
              indice={escadaEscolhida.indice}
              erro={modoDaEscada ? undefined : erroDoDesenho}
              dispatch={dispatch}
              onVoltar={() => setPecaSelecionada({ tipo: 'ESCADA', id: escadaSelecionada.id })}
              onFechar={() => setPecaSelecionada(undefined)}
            />
          )}

          {modo === 'VAO' && (
            <EmVao
              kind={vaoKind}
              largura={vaoLargura}
              altura={vaoAltura}
              onKind={(escolhido) => {
                setVaoKind(escolhido);
                setVaoLargura(MEDIDA_DO_VAO[escolhido].largura);
                setVaoAltura(MEDIDA_DO_VAO[escolhido].altura);
              }}
              onLargura={setVaoLargura}
              onAltura={setVaoAltura}
              onSair={() => setModo('PARADO')}
            />
          )}

          {modo === 'TRACANDO' && (
            <EmTracado
              nome={nome}
              kind={kind}
              altura={altura}
              peDireito={peDireito}
              outline={emMetros}
              delimitando={emDelimitacao?.name}
              onNome={setNome}
              onKind={setKind}
              onAltura={setAltura}
              onPeDireito={setPeDireito}
              onDesfazer={() => setTracado((atual) => atual.slice(0, -1))}
              onFechar={fechar}
              onCancelar={() => {
                setDelimitando(undefined);
                setTracado([]);
                setModo('PARADO');
              }}
            />
          )}

          {pecasDaPlanilha !== undefined && tipoDaFerramenta(modo) !== undefined && (
            <PecasDaPlanilhaDaFerramenta
              tipo={tipoDaFerramenta(modo)!}
              pecas={pecasDaPlanilha.pecas}
              posicionando={pecaNaMao}
              ocupado={pecasDaPlanilha.ocupado}
              erro={pecasDaPlanilha.erro}
              semEscala={escala === undefined}
              onPosicionar={setPosicionandoPeca}
              onCancelar={() => setPosicionandoPeca(undefined)}
              onTirar={pecasDaPlanilha.tirar}
              onRenomear={async (nome, novo) => {
                const renomeada = await pecasDaPlanilha.renomear(nome, novo);
                // A peça na mão continua na mão, com o nome novo: o próximo clique ainda é dela.
                if (renomeada !== undefined && posicionandoPeca === nome) {
                  setPosicionandoPeca(renomeada);
                }
                return renomeada;
              }}
            />
          )}

          <AmbientesDaTipologia
            draft={draft}
            colocadas={paredesColocadas}
            divisas={partyWallsTouching(workspace, draft)}
            onTrocarLado={trocarLado}
            dispatch={dispatch}
            selecionado={selecionado}
            delimitando={delimitando}
            temEscala={escala !== undefined}
            onSelecionar={setSelecionado}
            onDelimitar={delimitar}
            onExcluir={setAmbienteAExcluir}
            paredeSelecionada={paredeSelecionada}
            onSelecionarParede={(wallId) =>
              setParedeSelecionada((atual) => (atual === wallId ? undefined : wallId))
            }
            casarCom={casarCom}
            nomeDaDivisa={(wallId) => {
              const rotulo = workspace.partyLabels?.[wallId];
              return rotulo?.custom ?? rotulo?.a ?? 'Divisa';
            }}
          />

          <VaosEPecas
            draft={draft}
            tipos={workspace.elementTypes}
            selecionada={pecaSelecionada}
            onSelecionar={setPecaSelecionada}
            onExcluir={excluirPeca}
            // O tipo e o fator do pilar e da viga, na linha deles (Fase 42, RN-561 a RN-563).
            detalheDaPeca={(elemento) => (
              <TipoEFatoresDoElemento
                key={elemento.id}
                elemento={elemento}
                elementos={draft.elements}
                trechos={draft.elementSegments}
                nomeDaParede={nomeDaParedeDaMesa}
                tipos={workspace.elementTypes ?? []}
                {...(projectId === undefined ? {} : { projectId })}
                catalogo={workspace.catalog}
                aprovados={aprovados}
                media={mediaDoApartamento}
                erro={erroDoDesenho}
                dispatch={dispatch}
              />
            )}
          />
        </div>
      </div>

      <ConfirmDialog
        open={confirmandoRemocao}
        onOpenChange={setConfirmandoRemocao}
        title="Remover a prancha?"
        description={`${
          anexada === undefined
            ? 'A imagem e a escala saem da planta.'
            : avisoDeQuemAnexou(anexada, 'remover')
        } Os ambientes, paredes, vãos e peças já traçados continuam, com as medidas que têm — mas, para voltar a desenhar sobre eles, será preciso carregar a prancha de novo e calibrar.`}
        confirmLabel="Remover prancha"
        destructive
        onConfirm={() => {
          if (daObra !== undefined) {
            void daObra.remover().then((resultado) => {
              if (!resultado.ok) setErro(resultado.erro);
            });
          } else {
            dispatch({ type: 'CLEAR_PLAN' });
          }
          setModo('PARADO');
          setTracado([]);
          setCantos([]);
          setArrasto(undefined);
          setCalibragem([]);
          setConfirmandoRemocao(false);
        }}
      />

      {anexada !== undefined && (
        <ConfirmDialog
          open={confirmandoTroca}
          onOpenChange={setConfirmandoTroca}
          title="Trocar a prancha?"
          description={`${avisoDeQuemAnexou(anexada, 'trocar')} A folha nova chega sem escala e precisa ser calibrada; os ambientes, paredes, vãos e peças já traçados continuam, com as medidas que têm.`}
          confirmLabel="Escolher a prancha nova"
          onConfirm={() => {
            setConfirmandoTroca(false);
            // Ainda dentro do clique: o navegador só abre o seletor de arquivo para um
            // gesto da pessoa, e a confirmação é esse gesto.
            entrada.current?.click();
          }}
        />
      )}

      <ConfirmDialog
        open={ambienteAExcluir !== undefined}
        onOpenChange={(aberto) => {
          if (!aberto) setAmbienteAExcluir(undefined);
        }}
        title={`Excluir ${draft.rooms.find((room) => room.id === ambienteAExcluir)?.name ?? 'o ambiente'}?`}
        description="O ambiente sai com as paredes dele, e com os vãos e as peças que estavam nelas. A parede que ele dividia com outro ambiente fica, com o outro lado."
        confirmLabel="Excluir ambiente"
        destructive
        onConfirm={() => {
          if (ambienteAExcluir !== undefined) {
            dispatch({ type: 'REMOVE_ROOM', roomId: ambienteAExcluir });
            if (selecionado === ambienteAExcluir) setSelecionado(undefined);
          }
          setAmbienteAExcluir(undefined);
        }}
      />

      <ConfirmDialog
        open={ladoATrocar !== undefined}
        onOpenChange={(aberto) => {
          if (!aberto) setLadoATrocar(undefined);
        }}
        title={`Trocar o lado ${String((ladoATrocar?.edgeIndex ?? 0) + 1)} por ${
          ladoATrocar === undefined
            ? ''
            : EDGE_CHOICE_LABEL[ladoATrocar.kind].toLocaleLowerCase('pt-BR')
        }?`}
        description={`A parede deste lado sai, e com ela ${oQueSaiComAParede(
          ladoATrocar?.vaos ?? 0,
          ladoATrocar?.pecas ?? 0,
        )}. Voltar o lado para parede cria uma parede nova, sem eles.`}
        confirmLabel="Tirar a parede"
        destructive
        onConfirm={() => {
          if (ladoATrocar !== undefined) {
            dispatch({
              type: 'SET_EDGE_KIND',
              roomId: ladoATrocar.roomId,
              edgeIndex: ladoATrocar.edgeIndex,
              kind: ladoATrocar.kind,
            });
          }
          setLadoATrocar(undefined);
        }}
      />
    </>
  );
}

/** "1 vão e 2 peças" — o que a parede leva junto quando sai. */
function oQueSaiComAParede(vaos: number, pecas: number): string {
  const partes = [
    ...(vaos > 0 ? [vaos === 1 ? '1 vão' : `${String(vaos)} vãos`] : []),
    ...(pecas > 0
      ? [pecas === 1 ? '1 peça de estrutura' : `${String(pecas)} peças de estrutura`]
      : []),
  ];
  return partes.join(' e ');
}

// ── O card de cima: qual tipologia está na ponta do lápis ───────────────────────

/**
 * As tipologias da bancada, para trocar sem fechar a prancha.
 *
 * A prancha é do **pavimento**, e o pavimento tem vários apartamentos: desenhar o andar
 * inteiro era fechar o desenho, trocar a tipologia lá na página, reabrir a planta, achar
 * de novo o zoom e o lugar. Aqui a troca é um clique e o desenho não se move — o que já
 * foi traçado das outras continua à vista, apagado, servindo de referência e de encaixe
 * para a divisa fechar no mesmo ponto.
 *
 * As de **outro pavimento** aparecem na lista mas ficam marcadas: elas foram traçadas
 * sobre outra folha, e escolher uma delas é trocar de assunto, não de apartamento.
 */
function TipologiasDaPrancha({
  workspace,
  draft,
  dispatch,
}: {
  workspace: WorkspaceDraft;
  draft: TypologyDraft;
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const varios = floorKindsInUse(workspace).length > 1;
  // Na ordem do prédio, com as do pavimento em desenho na frente: é onde a mão está.
  const ordenadas = [...workspace.typologies].sort((uma, outra) => {
    const daMesa = (typology: TypologyDraft): number => (sameDrawing(typology, draft) ? -1 : 0);
    if (daMesa(uma) !== daMesa(outra)) return daMesa(uma) - daMesa(outra);
    return (
      FLOOR_PLAN_KIND_ORDER.indexOf(uma.floorKind) - FLOOR_PLAN_KIND_ORDER.indexOf(outra.floorKind)
    );
  });

  return (
    <section
      aria-label="Tipologia em desenho"
      className="flex shrink-0 items-center gap-2 overflow-x-auto rounded-md border border-(--color-border-subtle) bg-(--color-surface-muted) p-2"
    >
      <span className="shrink-0 pl-1 text-xs font-medium text-(--color-content-muted)">
        Desenhando
      </span>
      {ordenadas.map((typology) => {
        const naMesa = typology.unitTypeId === draft.unitTypeId;
        const tracados = typology.rooms.filter((room) => room.outline !== undefined).length;

        return (
          <button
            key={typology.unitTypeId}
            type="button"
            aria-current={naMesa ? 'true' : undefined}
            onClick={() => dispatch({ type: 'SELECT_TYPOLOGY', unitTypeId: typology.unitTypeId })}
            className={
              // Uma linha só, e não duas: a faixa cresce com o que ela mostra, e cada
              // pixel dela sai da prancha — que é onde o trabalho acontece.
              naMesa
                ? 'flex shrink-0 items-baseline gap-1.5 rounded-md border border-(--color-brand) bg-(--color-surface) px-2.5 py-1'
                : 'flex shrink-0 items-baseline gap-1.5 rounded-md border border-(--color-border-subtle) px-2.5 py-1 hover:bg-(--color-surface)'
            }
          >
            <span className={naMesa ? 'text-sm font-semibold' : 'text-sm'}>
              {nomeDaTipologia(typology)}
            </span>
            <span className="text-xs text-(--color-content-muted)">
              {tracados === 0
                ? 'nada traçado'
                : `${String(tracados)} de ${String(typology.rooms.length)}`}
              {varios && ` · ${FLOOR_PLAN_KIND_LABEL[typology.floorKind]}`}
            </span>
          </button>
        );
      })}
    </section>
  );
}

// ── Painel: o ambiente em traçado ───────────────────────────────────────────────

function EmTracado({
  nome,
  kind,
  altura,
  peDireito,
  outline,
  delimitando,
  onNome,
  onKind,
  onAltura,
  onPeDireito,
  onDesfazer,
  onFechar,
  onCancelar,
}: {
  nome: string;
  kind: RoomKind;
  altura: string;
  peDireito: string;
  outline: readonly Point[];
  /** O nome do ambiente sendo delimitado, quando é um que já existe. */
  delimitando: string | undefined;
  onNome: (valor: string) => void;
  onKind: (valor: RoomKind) => void;
  onAltura: (valor: string) => void;
  onPeDireito: (valor: string) => void;
  onDesfazer: () => void;
  onFechar: () => void;
  onCancelar: () => void;
}) {
  const lados = polygonEdges(outline);
  const fechado = outline.length >= 3;

  return (
    <section className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3">
      <h3 className="text-sm font-semibold">
        {delimitando === undefined ? 'Ambiente novo' : `Delimitando ${delimitando}`}
      </h3>

      {/*
        Nome, tipo e pé-direito são do ambiente **novo**. Quem está delimitando um que já
        existe não vem aqui redefini-lo — ele já foi cadastrado, e repetir os campos
        convidaria a criar uma segunda versão dele sem querer.
      */}
      {delimitando === undefined ? (
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Nome" htmlFor="tracado-nome">
            <Input
              id="tracado-nome"
              value={nome}
              placeholder={ROOM_KIND_LABEL[kind]}
              onChange={(evento) => onNome(evento.target.value)}
              className="w-32"
            />
          </Field>
          <Field label="Tipo" htmlFor="tracado-tipo">
            <select
              id="tracado-tipo"
              value={kind}
              onChange={(evento) => onKind(evento.target.value as RoomKind)}
              className={SELECT_CLASS}
            >
              {ROOM_KINDS.map((item) => (
                <option key={item} value={item}>
                  {ROOM_KIND_LABEL[item]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Altura" htmlFor="tracado-altura">
            <Input
              id="tracado-altura"
              inputMode="decimal"
              value={altura}
              onChange={(evento) => onAltura(evento.target.value)}
              className="w-20"
            />
          </Field>
          <Field label="Pé-direito" htmlFor="tracado-pe">
            <Input
              id="tracado-pe"
              inputMode="decimal"
              value={peDireito}
              onChange={(evento) => onPeDireito(evento.target.value)}
              className="w-20"
            />
          </Field>
        </div>
      ) : (
        <p className="text-xs text-(--color-content-muted)">
          Clique os cantos deste ambiente na planta. O contorno substitui o que ele tinha — medidas,
          área e paredes livres passam a sair do traço.
        </p>
      )}

      <dl className="flex flex-col gap-1 border-t border-(--color-border-subtle) pt-2 text-sm">
        <Linha rotulo="Pontos" valor={String(outline.length)} />
        <Linha
          rotulo="Área"
          valor={fechado ? `${polygonArea(outline).toFixed(2)} m²` : '—'}
          destaque
        />
        <Linha
          rotulo="Perímetro"
          valor={fechado ? `${polygonPerimeter(outline).toFixed(2)} m` : '—'}
        />
      </dl>

      {lados.length > 0 && (
        <ol className="flex flex-col gap-0.5 text-xs text-(--color-content-muted)">
          {lados.map((lado, indice) => (
            <li key={indice} className="flex justify-between">
              <span>Lado {indice + 1}</span>
              <strong className="text-(--color-content)">{segmentLength(lado).toFixed(2)} m</strong>
            </li>
          ))}
        </ol>
      )}

      <p className="text-xs text-(--color-content-muted)">Clique no primeiro ponto para fechar.</p>

      <div className="flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" disabled={outline.length === 0} onClick={onDesfazer}>
          <Undo2 className="size-4" /> Desfazer ponto
        </Button>
        <Button variant="primary" size="sm" disabled={!fechado} onClick={onFechar}>
          {delimitando === undefined ? 'Fechar ambiente' : 'Aplicar contorno'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </section>
  );
}

// ── Painel: o que já foi traçado, e as paredes de cada um ───────────────────────

function AmbientesDaTipologia({
  draft,
  colocadas,
  divisas,
  onTrocarLado,
  dispatch,
  selecionado,
  delimitando,
  temEscala,
  onSelecionar,
  onDelimitar,
  onExcluir,
  paredeSelecionada,
  onSelecionarParede,
  casarCom,
  nomeDaDivisa,
}: {
  draft: TypologyDraft;
  /** Onde o desenho pôs cada parede — o que falta aqui é a parede que "sumiu" (RN-245). */
  colocadas: readonly PlacedWall[];
  /** As divisas com outras tipologias — o lado que elas ocupam é sempre parede (RN-269). */
  divisas: readonly Wall[];
  /** Troca o tipo de um lado — quem pergunta antes de tirar a parede é a tela. */
  onTrocarLado: (roomId: RoomId, edgeIndex: number, kind: EdgeChoice) => void;
  dispatch: React.Dispatch<WorkspaceAction>;
  selecionado: RoomId | undefined;
  delimitando: RoomId | undefined;
  temEscala: boolean;
  onSelecionar: (roomId: RoomId) => void;
  onDelimitar: (roomId: RoomId) => void;
  /** Pede a exclusão — quem confirma é a tela, porque ela leva paredes e vãos junto. */
  onExcluir: (roomId: RoomId) => void;
  /** A parede na mão, e quem a troca — o mesmo estado que o clique na prancha mexe. */
  paredeSelecionada?: WallId | undefined;
  onSelecionarParede?: (wallId: WallId) => void;
  /** As candidatas a casar com a parede na mão, da mais perto para a mais longe (RN-306). */
  casarCom?: (wall: Wall) => readonly CandidataParaCasar[];
  /** O nome da divisa com outra tipologia — ela mora na bancada, e o rótulo também. */
  nomeDaDivisa?: (wallId: WallId) => string;
}) {
  if (draft.rooms.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-(--color-border-subtle) p-3 text-xs text-(--color-content-muted)">
        Nenhum ambiente ainda. Trace um novo aqui, ou cadastre-o na lista de ambientes e volte para
        delimitá-lo na planta.
      </p>
    );
  }

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">Ambientes de {nomeDaTipologia(draft)}</h3>
      {/*
        Todos, e não só os traçados: é esta lista que responde "o que já está na planta e o
        que ainda não está". Mostrando só os traçados, o ambiente cadastrado à mão sumia
        daqui justamente enquanto se procurava por onde delimitá-lo.
      */}
      {draft.rooms.map((room) => {
        const aberto = room.id === selecionado;
        const tracado = room.outline !== undefined;
        const paredes = draft.walls.filter(
          (wall) => wall.sideA.roomId === room.id || wall.sideB?.roomId === room.id,
        );
        // A parede que conta no painel e soma no total, mas que o desenho não tem onde pôr.
        // Antes ela sumia calada, e ninguém sabia por quê (D22); agora a tela diz.
        const semLugar = new Set(
          unplacedWalls(room, draft.walls, colocadas).map((wall) => String(wall.id)),
        );
        // Os buracos no chão deste ambiente — o piso e o teto dele já saem sem eles (RN-271).
        const shafts = (draft.shafts ?? []).filter((shaft) => shaft.roomId === room.id);

        return (
          <div
            key={room.id}
            className={
              aberto
                ? 'rounded-md border border-(--color-brand) p-3'
                : 'rounded-md border border-(--color-border-subtle) p-3'
            }
          >
            <button
              type="button"
              className="flex w-full items-baseline justify-between gap-2 text-left"
              onClick={() => onSelecionar(room.id)}
            >
              <span className="text-sm font-medium">{room.name}</span>
              <span className="text-xs text-(--color-content-muted)">
                {tracado ? resumoDoAmbiente(room, paredes.length) : 'sem contorno na planta'}
                {shafts.length > 0 &&
                  ` · ${String(shafts.length)} ${shafts.length === 1 ? 'shaft' : 'shafts'}`}
              </span>
            </button>

            <div className="mt-2 flex flex-wrap items-center gap-1">
              <Button
                variant={tracado ? 'ghost' : 'secondary'}
                size="sm"
                disabled={!temEscala || delimitando === room.id}
                onClick={() => onDelimitar(room.id)}
              >
                <Crosshair className="size-4" />
                {tracado ? 'Redesenhar' : 'Delimitar na planta'}
              </Button>
              {tracado && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => dispatch({ type: 'CLEAR_ROOM_OUTLINE', roomId: room.id })}
                >
                  Descartar contorno
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Excluir ${room.name}`}
                onClick={() => onExcluir(room.id)}
              >
                <Trash2 className="size-4" /> Excluir
              </Button>
            </div>

            {semLugar.size > 0 && (
              <p className="mt-2">
                <Badge tone="warn">
                  {semLugar.size === 1
                    ? '1 parede sem lugar no desenho'
                    : `${String(semLugar.size)} paredes sem lugar no desenho`}
                </Badge>
              </p>
            )}

            {aberto && (
              <ul className="mt-2 flex flex-col gap-1 border-t border-(--color-border-subtle) pt-2">
                {paredes.map((wall) => (
                  <li key={wall.id} className="flex flex-col gap-1 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <NomeDaParede
                        nome={draft.labels[wall.id]?.custom ?? draft.labels[wall.id]?.a ?? 'Parede'}
                        selecionada={wall.id === paredeSelecionada}
                        {...(onSelecionarParede === undefined
                          ? {}
                          : { onSelecionar: () => onSelecionarParede(wall.id) })}
                      />
                      <span className="flex shrink-0 items-center gap-1">
                        {semLugar.has(String(wall.id)) && <Badge tone="warn">sem lugar</Badge>}
                        {wall.sideB !== undefined && <Badge tone="info">casada</Badge>}
                        {wall.joined && <Badge tone="warn">conjugada</Badge>}
                        <Input
                          aria-label={`Comprimento da parede de ${room.name}`}
                          inputMode="decimal"
                          value={String(Number(wall.length.toFixed(2)))}
                          onChange={(evento) =>
                            dispatch({
                              type: 'UPDATE_WALL',
                              wallId: wall.id,
                              patch: {
                                length:
                                  Number(evento.target.value.replace(',', '.')) || wall.length,
                              },
                            })
                          }
                          className="h-8 w-20 md:h-8"
                        />
                        m
                      </span>
                    </div>
                    {wall.id === paredeSelecionada && casarCom !== undefined && (
                      <CasarParede
                        wall={wall}
                        room={room}
                        draft={draft}
                        candidatas={casarCom(wall)}
                        dispatch={dispatch}
                      />
                    )}
                  </li>
                ))}
                {/*
                  A divisa com outra tipologia é parede deste ambiente como as outras, e mora na
                  bancada. Aparece aqui para ser descasada pelo mesmo caminho (RN-306).
                */}
                {divisas
                  .filter((wall) => wallSideOf(wall, room.id) !== undefined)
                  .map((wall) => (
                    <li key={wall.id} className="flex flex-col gap-1 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <NomeDaParede
                          nome={nomeDaDivisa?.(wall.id) ?? 'Divisa'}
                          selecionada={wall.id === paredeSelecionada}
                          {...(onSelecionarParede === undefined
                            ? {}
                            : { onSelecionar: () => onSelecionarParede(wall.id) })}
                        />
                        <span className="flex shrink-0 items-center gap-1">
                          <Badge tone="warn">divisa com outra tipologia</Badge>
                          {String(Number(wall.length.toFixed(2))).replace('.', ',')} m
                        </span>
                      </div>
                      {wall.id === paredeSelecionada && (
                        <CasarParede
                          wall={wall}
                          room={room}
                          draft={draft}
                          candidatas={[]}
                          dispatch={dispatch}
                        />
                      )}
                    </li>
                  ))}
              </ul>
            )}

            {aberto && tracado && (
              <LadosDoAmbiente
                room={room}
                walls={draft.walls}
                divisas={divisas}
                dispatch={dispatch}
                onTrocar={(edgeIndex, kind) => onTrocarLado(room.id, edgeIndex, kind)}
              />
            )}
            {aberto && shafts.length > 0 && (
              <ul
                className="mt-2 flex flex-col gap-1 border-t border-(--color-border-subtle) pt-2"
                aria-label={`Shafts de ${room.name}`}
              >
                {shafts.map((shaft) => (
                  <li key={shaft.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate">
                      Shaft {cm(shaftWidth(shaft))} × {cm(shaftLength(shaft))} cm ·{' '}
                      {shaftArea(shaft).toFixed(2)} m² · {shaftContour(shaft).toFixed(2)} m
                    </span>
                    <span className="flex shrink-0 items-center gap-1">
                      <Input
                        aria-label={`Quantos shafts iguais em ${room.name}`}
                        inputMode="numeric"
                        value={String(shaft.count)}
                        onChange={(evento) =>
                          dispatch({
                            type: 'SET_SHAFT_COUNT',
                            shaftId: shaft.id,
                            count: Number(evento.target.value),
                          })
                        }
                        className="h-8 w-14 md:h-8"
                      />
                      un
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Excluir shaft de ${room.name}`}
                        onClick={() => dispatch({ type: 'REMOVE_SHAFT', shaftId: shaft.id })}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </section>
  );
}

/** O nome da parede no painel — um botão que a põe na mão, quando há quem a ponha. */
function NomeDaParede({
  nome,
  selecionada,
  onSelecionar,
}: {
  nome: string;
  selecionada: boolean;
  onSelecionar?: () => void;
}) {
  if (onSelecionar === undefined) return <span className="truncate">{nome}</span>;
  return (
    <button
      type="button"
      aria-pressed={selecionada}
      className={
        selecionada
          ? 'truncate rounded-sm text-left font-semibold text-(--color-brand)'
          : 'truncate rounded-sm text-left hover:underline'
      }
      onClick={onSelecionar}
    >
      {nome}
    </button>
  );
}

/**
 * Casar e descasar a parede na mão (Fase 24, fatia 8 · D24 · RN-306).
 *
 * As candidatas vêm da mais perto para a mais longe, cada uma dizendo a que distância está
 * e quanto corre junto desta — o número que vai virar a parede casada. Separadas em duas
 * listas porque as consequências são diferentes: dentro do apto, a parede vira uma só;
 * com outra tipologia, vira divisa do pavimento, que se reparte meio a meio (RN-163).
 *
 * Se as duas não cobrem o mesmo trecho, o casamento corta o que sobra de cada uma, e o
 * resto continua parede livre do ambiente dela (RN-305). Descasar devolve as duas e junta
 * de novo os pedaços do mesmo lado.
 */
function CasarParede({
  wall,
  room,
  draft,
  candidatas,
  dispatch,
}: {
  wall: Wall;
  room: Room;
  draft: TypologyDraft;
  candidatas: readonly CandidataParaCasar[];
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  if (wall.sideB !== undefined) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md bg-(--color-surface-muted) px-2 py-1">
        <span className="text-(--color-content-muted)">
          {String(Number(wall.length.toFixed(2))).replace('.', ',')} m casados
          {wall.joined ? ' · conjugada' : ''}
        </span>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Descasar a parede de ${room.name}`}
          onClick={() => dispatch({ type: 'UNMERGE_WALL', wallId: wall.id })}
        >
          <Link2Off className="size-4" /> Descasar
        </Button>
      </div>
    );
  }

  const daqui = candidatas.filter(({ typology }) => typology.unitTypeId === draft.unitTypeId);
  const deFora = candidatas.filter(({ typology }) => typology.unitTypeId !== draft.unitTypeId);
  const opcao = ({ wall: outra, typology, distance, shared }: CandidataParaCasar) => (
    <option key={outra.id} value={outra.id}>
      {typology.unitTypeId === draft.unitTypeId ? '' : `${typology.name} · `}
      {wallShortName(typology.labels[outra.id])} · a {String(Math.round(distance * 100))} cm ·{' '}
      {shared.toFixed(2).replace('.', ',')} m em comum
    </option>
  );

  return (
    <select
      value=""
      aria-label={`Casar a parede de ${room.name} com`}
      disabled={candidatas.length === 0}
      onChange={(evento) => {
        if (evento.target.value === '') return;
        dispatch({
          type: 'MERGE_WALLS',
          keepId: wall.id,
          dropId: evento.target.value as WallId,
        });
      }}
      className={`${SELECT_CLASS} md:h-8`}
    >
      <option value="">
        {candidatas.length === 0 ? 'Nenhuma parede livre correndo junto' : 'Casar com…'}
      </option>
      {daqui.length > 0 && (
        <optgroup label={`Dentro de ${draft.name}`}>{daqui.map(opcao)}</optgroup>
      )}
      {deFora.length > 0 && (
        <optgroup label="Divisa com outra tipologia">{deFora.map(opcao)}</optgroup>
      )}
    </select>
  );
}

/**
 * Área, perímetro e o que fecha o ambiente, numa linha.
 *
 * O perímetro é o que leva rodapé — sem os lados de guarda-corpo e abertos (RN-267) —, e
 * não o do contorno inteiro: é o número que o orçamento vai usar, e mostrar outro aqui
 * faria a varanda parecer ter nove metros de rodapé.
 */
function resumoDoAmbiente(room: Room, paredes: number): string {
  const contorno = room.outline ?? [];
  const semParede = edgesWithoutWallOf(room);
  const perimetro =
    polygonPerimeter(contorno) - semParede.reduce((total, { length }) => total + length, 0);
  const guardaCorpos = semParede.filter(({ edge }) => edge.kind === 'GUARDA_CORPO').length;
  const abertos = semParede.length - guardaCorpos;
  return [
    `${polygonArea(contorno).toFixed(2)} m²`,
    `${perimetro.toFixed(2)} m`,
    `${String(paredes)} paredes`,
    ...(guardaCorpos > 0
      ? [`${String(guardaCorpos)} guarda-corpo${guardaCorpos === 1 ? '' : 's'}`]
      : []),
    ...(abertos > 0 ? [abertos === 1 ? '1 lado aberto' : `${String(abertos)} lados abertos`] : []),
  ].join(' · ');
}

/** As quatro respostas para "o que fecha este lado", na ordem de quem pergunta. */
const EDGE_CHOICES: readonly EdgeChoice[] = ['PAREDE', 'MURETA', 'GUARDA_CORPO', 'ABERTO'];

/**
 * Os lados do contorno, e o que cada um é (D25).
 *
 * Todo lado nasce parede. A varanda com sacada é o caso que trouxe a pergunta: a frente
 * dela é guarda-corpo, e a parede de 2,90 que o traço punha ali somava bloco, reboco e
 * rodapé que a obra não faz. A mureta mostra as duas alturas — a dela, que é alvenaria, e
 * a do guarda-corpo em cima. A divisa não se escolhe: é sempre parede (RN-269).
 */
function LadosDoAmbiente({
  room,
  walls,
  divisas,
  dispatch,
  onTrocar,
}: {
  room: Room;
  walls: readonly Wall[];
  divisas: readonly Wall[];
  dispatch: React.Dispatch<WorkspaceAction>;
  onTrocar: (edgeIndex: number, kind: EdgeChoice) => void;
}) {
  const lados = polygonEdges(room.outline ?? []);

  return (
    <section
      aria-label={`Lados de ${room.name}`}
      className="mt-2 flex flex-col gap-1.5 border-t border-(--color-border-subtle) pt-2"
    >
      <h4 className="text-xs font-medium text-(--color-content-muted)">O que fecha cada lado</h4>
      <ul className="flex flex-col gap-2">
        {lados.map((segmento, indice) => {
          const nome = `Lado ${String(indice + 1)}`;
          const paredes = wallsOnEdge(room, walls, indice);
          const divisa =
            paredes.some((wall) => wall.sideB !== undefined) ||
            divisas.some((wall) => wallSideOf(wall, room.id)?.edgeIndex === indice);
          const guardaCorpo = edgeWithoutWallAt(room, indice);
          const mureta = paredes.find((wall) => wall.railingHeight !== undefined);

          return (
            <li key={indice} className="flex flex-col gap-1 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span>
                  {nome} · {segmentLength(segmento).toFixed(2)} m
                </span>
                {divisa ? (
                  <Badge tone="info">divisa · sempre parede</Badge>
                ) : (
                  <select
                    aria-label={`O que fecha o ${nome.toLocaleLowerCase('pt-BR')} de ${room.name}`}
                    value={edgeChoiceOf(room, walls, indice)}
                    onChange={(evento) => onTrocar(indice, evento.target.value as EdgeChoice)}
                    className={`${SELECT_CLASS} md:h-8`}
                  >
                    {EDGE_CHOICES.map((escolha) => (
                      <option key={escolha} value={escolha}>
                        {EDGE_CHOICE_LABEL[escolha]}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {guardaCorpo?.kind === 'GUARDA_CORPO' && (
                <span className="flex items-center justify-end gap-1 text-(--color-content-muted)">
                  guarda-corpo de
                  <NumeroInput
                    aria-label={`Altura do guarda-corpo do ${nome.toLocaleLowerCase('pt-BR')} de ${room.name}`}
                    value={guardaCorpo.height}
                    onChange={(height) =>
                      dispatch({
                        type: 'SET_EDGE_RAILING_HEIGHT',
                        roomId: room.id,
                        edgeIndex: indice,
                        height,
                      })
                    }
                    className="h-8 w-16 md:h-8"
                  />
                  m de altura
                </span>
              )}

              {mureta !== undefined && (
                <span className="flex flex-wrap items-center justify-end gap-1 text-(--color-content-muted)">
                  mureta de
                  <NumeroInput
                    aria-label={`Altura da mureta do ${nome.toLocaleLowerCase('pt-BR')} de ${room.name}`}
                    value={mureta.height}
                    onChange={(height) =>
                      dispatch({ type: 'UPDATE_WALL', wallId: mureta.id, patch: { height } })
                    }
                    className="h-8 w-16 md:h-8"
                  />
                  m, guarda-corpo de
                  <NumeroInput
                    aria-label={`Altura do guarda-corpo sobre a mureta do ${nome.toLocaleLowerCase('pt-BR')} de ${room.name}`}
                    value={mureta.railingHeight ?? 0}
                    onChange={(railingHeight) =>
                      dispatch({ type: 'UPDATE_WALL', wallId: mureta.id, patch: { railingHeight } })
                    }
                    className="h-8 w-16 md:h-8"
                  />
                  m
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Linha({
  rotulo,
  valor,
  destaque = false,
}: {
  rotulo: string;
  valor: string;
  destaque?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-(--color-content-muted)">{rotulo}</dt>
      <dd className={destaque ? 'font-semibold' : 'font-medium'}>{valor}</dd>
    </div>
  );
}

function PoligonoPronto({
  room,
  escala,
  zoom,
  selecionado,
  onSelecionar,
}: {
  room: Room;
  escala: number;
  /** O traço e o rótulo encolhem na mesma proporção, para não engordar com a ampliação. */
  zoom: number;
  selecionado: boolean;
  /** Ausente enquanto se traça: aí o clique é do traço, e o polígono deixa passar. */
  onSelecionar: (() => void) | undefined;
}) {
  const pixels = (room.outline ?? []).map((ponto) => metresToPixel(ponto, escala));
  const centro = pixels.reduce(
    (soma, ponto) => ({ x: soma.x + ponto.x / pixels.length, y: soma.y + ponto.y / pixels.length }),
    { x: 0, y: 0 },
  );

  return (
    <g
      onClick={onSelecionar}
      className={onSelecionar === undefined ? 'pointer-events-none' : 'cursor-pointer'}
    >
      <polygon
        points={pixels.map((ponto) => `${String(ponto.x)},${String(ponto.y)}`).join(' ')}
        fill={selecionado ? CORES.ambienteSelecionado : CORES.ambiente}
        stroke={selecionado ? CORES.ambienteSelecionado : CORES.ambiente}
        fillOpacity={selecionado ? 0.26 : 0.12}
        strokeWidth={1.5 / zoom}
      />
      <Rotulo
        texto={room.name}
        em={centro}
        zoom={zoom}
        cor={selecionado ? CORES.ambienteSelecionado : CORES.ambiente}
        tamanho={12}
        peso={600}
      />
    </g>
  );
}

/**
 * A tipologia vizinha, apagada atrás do trabalho.
 *
 * Ela não é editável daqui e não devia parecer que é: sem preenchimento forte, sem
 * rótulo de parede, sem estrutura — só o contorno e o nome, que é o que responde "onde
 * acaba o apto 01 e começa o 02". Clicar nela troca a tipologia em desenho, que é o
 * gesto que a pessoa já ia fazer olhando para ali.
 */
function TipologiaAoFundo({
  typology,
  escala,
  zoom,
  onSelecionar,
}: {
  typology: TypologyDraft;
  escala: number;
  zoom: number;
  /** Ausente enquanto se traça: aí o clique é do traço, e o polígono deixa passar. */
  onSelecionar: (() => void) | undefined;
}) {
  return (
    <g
      onClick={onSelecionar}
      className={onSelecionar === undefined ? 'pointer-events-none' : 'cursor-pointer'}
    >
      {typology.rooms.map((room) => {
        if (room.outline === undefined) return null;
        const pixels = room.outline.map((ponto) => metresToPixel(ponto, escala));
        const centro = pixels.reduce(
          (soma, ponto) => ({
            x: soma.x + ponto.x / pixels.length,
            y: soma.y + ponto.y / pixels.length,
          }),
          { x: 0, y: 0 },
        );

        return (
          <g key={room.id}>
            <polygon
              points={pixels.map((ponto) => `${String(ponto.x)},${String(ponto.y)}`).join(' ')}
              fill={CORES.vizinha}
              fillOpacity={0.08}
              stroke={CORES.vizinha}
              strokeWidth={1 / zoom}
              strokeDasharray={`${4 / zoom} ${3 / zoom}`}
            >
              <title>
                {typology.name} — {room.name}
              </title>
            </polygon>
            <Rotulo
              texto={`${typology.name} · ${room.name}`}
              em={centro}
              zoom={zoom}
              cor={CORES.vizinha}
              tamanho={9}
            />
          </g>
        );
      })}
    </g>
  );
}

// ── O desenho colorido: cada peça com a cor dela ────────────────────────────────

/**
 * As cores do traçado **não seguem o tema**, e isso é de propósito.
 *
 * O tema escuro pinta a tela, não a prancha: a planta continua sendo papel branco com
 * linha preta, e as cores claras do modo escuro sumiriam justamente sobre ela. Então a
 * paleta é fixa, escolhida para ler sobre branco — e é a mesma da legenda, que é o que
 * torna "isto é viga, aquilo é pilar" uma pergunta respondida pelo desenho.
 */
const CORES = {
  ambiente: '#2563eb',
  ambienteSelecionado: '#1d4ed8',
  /** A tipologia vizinha: cinza, porque ela está aqui para ser vista, não mexida. */
  vizinha: '#64748b',
  /** O traço fora de esquadro — a serrilha que pede uma segunda olhada. */
  torto: '#b45309',
  parede: '#c2410c',
  /** O guarda-corpo: tracejado fino, e numa cor que não é de parede nem de vão. */
  guardaCorpo: '#be185d',
  PORTA: '#0f766e',
  JANELA: '#0891b2',
  PASSAGEM: '#65a30d',
  PILAR: '#b91c1c',
  VIGA: '#7c3aed',
  /**
   * O buraco na laje: magenta (fúcsia), que lê sobre o papel branco — e não o rosa do
   * guarda-corpo, que chegou junto (fatia 9) e é outra peça.
   */
  SHAFT: '#a21caf',
} as const;

/** A espessura mínima do traço da parede, em pixels de tela — abaixo disso não se enxerga. */
const TRACO_MINIMO = 2.5;

/**
 * As paredes desenhadas com a espessura que têm.
 *
 * O contorno do ambiente já era uma linha; a parede não é: ela tem 9, 15 ou 20 cm, e ver
 * isso na prancha é o que denuncia a parede que ficou com a espessura errada — invisível
 * numa lista de números, óbvia quando a de 9 aparece do mesmo tamanho da de 20.
 *
 * A conjugada (RN-161) sai tracejada: ali a parede **não existe**, e desenhá-la cheia
 * diria o contrário do que o cadastro diz.
 */
function ParedesDesenhadas({
  paredes,
  porId,
  nomeDaParede,
  escala,
  zoom,
  mostrarNomes,
  selecionada,
  onSelecionar,
}: {
  paredes: readonly PlacedWall[];
  porId: ReadonlyMap<string, Wall>;
  nomeDaParede: (wallId: WallId) => string;
  escala: number;
  zoom: number;
  mostrarNomes: boolean;
  /** A parede na mão — a faixa dela sai na cor da seleção (Fase 24, fatia 8). */
  selecionada?: WallId | undefined;
  /** Clicar na faixa põe a parede na mão. Ausente, a parede não é clicável. */
  onSelecionar?: (posta: PlacedWall) => void;
}) {
  return (
    <g>
      {paredes.map((posta) => {
        const { wallId, segment } = posta;
        const wall = porId.get(String(wallId));
        const naMao = wallId === selecionada;
        const de = metresToPixel(segment.from, escala);
        const ate = metresToPixel(segment.to, escala);
        const espessura = Math.max(
          (wall?.thickness ?? DEFAULT_WALL_THICKNESS) / escala,
          TRACO_MINIMO / zoom,
        );

        // Fora de esquadro ganha o dente de serra por cima do eixo: a faixa continua
        // mostrando a espessura, e a serrilha mostra que ela está torta.
        const torta = !isSquare(segment);

        return (
          <g key={String(wallId)}>
            <line
              x1={de.x}
              y1={de.y}
              x2={ate.x}
              y2={ate.y}
              stroke={naMao ? CORES.ambienteSelecionado : CORES.parede}
              strokeWidth={espessura}
              strokeOpacity={naMao ? 0.9 : wall?.joined === true ? 0.3 : 0.55}
              strokeLinecap="butt"
              {...(wall?.joined === true ? { strokeDasharray: `${6 / zoom} ${4 / zoom}` } : {})}
              {...(onSelecionar === undefined
                ? {}
                : {
                    className: 'cursor-pointer',
                    // Parado aqui: o clique na faixa é da parede, e não do vazio da prancha,
                    // que desfaria a seleção.
                    onClick: (evento: MouseEvent<SVGLineElement>) => {
                      evento.stopPropagation();
                      onSelecionar(posta);
                    },
                  })}
            >
              <title>
                {nomeDaParede(wallId)} — {segmentLength(segment).toFixed(2)} m
                {torta ? ' · fora de esquadro' : ''}
              </title>
            </line>
            {torta && (
              <path
                d={serratedPath(de, ate, 3 / zoom, 9 / zoom)}
                fill="none"
                stroke={CORES.torto}
                strokeWidth={1.5 / zoom}
                strokeLinejoin="round"
                className="pointer-events-none"
              />
            )}
            {mostrarNomes && (
              <RotuloDaParede
                texto={nomeDaParede(wallId)}
                de={de}
                ate={ate}
                zoom={zoom}
                cor={CORES.parede}
              />
            )}
          </g>
        );
      })}
    </g>
  );
}

/**
 * Um lado do traço em andamento — reto, ou serrilhado quando está torto.
 *
 * A serrilha é o aviso que falta na hora em que ele serve: uma parede de 3,00 e outra de
 * 3,02 são idênticas no desenho e diferentes no orçamento, e o dente de serra denuncia o
 * desalinhamento enquanto o ponto ainda pode ser desfeito. O segmento **vivo** — do
 * último ponto ao cursor — é o que mais importa: ali ainda nem clique houve.
 */
/**
 * O guarda-corpo no desenho: tracejado fino, na cor dele (D25).
 *
 * Fino porque não é parede — a faixa grossa é o que diz "bloco", e o guarda-corpo não tem
 * bloco. O do lado sem parede corre no lado do contorno; o de cima da mureta, por cima da
 * faixa dela, porque os dois estão lá. O lado aberto não tem traço nenhum: ali não há
 * nada, e o contorno do ambiente já diz onde ele acaba.
 */
function GuardaCorposDesenhados({
  rooms,
  paredes,
  porId,
  escala,
  zoom,
}: {
  rooms: readonly Room[];
  paredes: readonly PlacedWall[];
  porId: ReadonlyMap<string, Wall>;
  escala: number;
  zoom: number;
}) {
  const trechos = [
    ...rooms.flatMap((room) =>
      edgesWithoutWallOf(room)
        .filter(({ edge }) => edge.kind === 'GUARDA_CORPO')
        .map(({ edge, segment }) => ({
          chave: `lado-${String(room.id)}-${String(edge.edgeIndex)}`,
          segment,
        })),
    ),
    ...paredes
      .filter(({ wallId }) => porId.get(String(wallId))?.railingHeight !== undefined)
      .map(({ wallId, segment }) => ({ chave: `mureta-${String(wallId)}`, segment })),
  ];

  return (
    <g className="pointer-events-none">
      {trechos.map(({ chave, segment }) => {
        const de = metresToPixel(segment.from, escala);
        const ate = metresToPixel(segment.to, escala);
        return (
          <line
            key={chave}
            x1={de.x}
            y1={de.y}
            x2={ate.x}
            y2={ate.y}
            stroke={CORES.guardaCorpo}
            strokeWidth={1.5 / zoom}
            strokeDasharray={`${6 / zoom} ${3 / zoom}`}
            strokeLinecap="butt"
          >
            <title>Guarda-corpo</title>
          </line>
        );
      })}
    </g>
  );
}

function TracoEmAndamento({
  de,
  ate,
  zoom,
  escala,
  vivo,
}: {
  de: Pixel;
  ate: Pixel;
  zoom: number;
  escala: number;
  vivo: boolean;
}) {
  const emMetros = {
    from: { x: de.x * escala, y: de.y * escala },
    to: { x: ate.x * escala, y: ate.y * escala },
  };
  const torto = !isSquare(emMetros);

  if (!torto) {
    return (
      <line
        x1={de.x}
        y1={de.y}
        x2={ate.x}
        y2={ate.y}
        stroke={CORES.ambiente}
        strokeWidth={2 / zoom}
        {...(vivo ? { strokeOpacity: 0.7 } : {})}
      />
    );
  }

  return (
    <path
      d={serratedPath(de, ate, 3 / zoom, 9 / zoom)}
      fill="none"
      stroke={CORES.torto}
      strokeWidth={2 / zoom}
      strokeLinejoin="round"
    >
      <title>Fora de esquadro</title>
    </path>
  );
}

/**
 * A parede empurrada, enquanto ela segue o mouse.
 *
 * Mostra as duas coisas que decidem o clique: **onde ela vai parar** — o contorno inteiro
 * previsto, porque empurrar um lado estica os dois vizinhos — e **quanto andou**, em
 * centímetros, que é como se fala de ajuste de desenho em obra.
 */
function ParedeEmpurrada({
  alvo,
  distancia,
  empurrados,
  areaDoAlvo,
  escala,
  zoom,
}: {
  alvo: EdgeRef;
  distancia: number;
  empurrados: readonly SlidOutline[];
  /** A área do ambiente antes e depois — é ela que diz se o empurrão cresce ou encolhe. */
  areaDoAlvo: { readonly antes: number; readonly depois: number } | undefined;
  escala: number;
  zoom: number;
}) {
  const de = metresToPixel(alvo.segment.from, escala);
  const ate = metresToPixel(alvo.segment.to, escala);
  const meio = { x: (de.x + ate.x) / 2, y: (de.y + ate.y) / 2 };
  const cresceu = (areaDoAlvo?.depois ?? 0) >= (areaDoAlvo?.antes ?? 0);

  return (
    <g className="pointer-events-none">
      {empurrados.map(({ roomId, outline }) => (
        <polygon
          key={String(roomId)}
          points={outline
            .map((ponto) => {
              const pixel = metresToPixel(ponto, escala);
              return `${String(pixel.x)},${String(pixel.y)}`;
            })
            .join(' ')}
          fill={CORES.ambienteSelecionado}
          fillOpacity={0.14}
          stroke={CORES.ambienteSelecionado}
          strokeWidth={2 / zoom}
          strokeDasharray={`${6 / zoom} ${4 / zoom}`}
        />
      ))}
      <line
        x1={de.x}
        y1={de.y}
        x2={ate.x}
        y2={ate.y}
        stroke={CORES.ambienteSelecionado}
        strokeWidth={4 / zoom}
        strokeOpacity={0.5}
      />
      <Rotulo
        texto={`${cresceu ? '+' : '−'}${String(Math.abs(Math.round(distancia * 100)))} cm · ${(
          areaDoAlvo?.depois ?? 0
        ).toFixed(2)} m²`}
        em={{ x: meio.x, y: meio.y - 10 / zoom }}
        zoom={zoom}
        cor={CORES.ambienteSelecionado}
        tamanho={12}
        peso={700}
      />
    </g>
  );
}

/**
 * O nome da parede, deitado sobre ela.
 *
 * Deitado, e não solto num canto: numa planta com dezoito paredes, um rótulo que não
 * acompanha o traço obriga a adivinhar de quem ele é. E **some quando não cabe** — texto
 * mais largo que a parede vira rabisco por cima do desenho, que é pior do que não ter
 * nome nenhum. Quem precisa dele amplia, e ele aparece: a fonte encolhe com o zoom, o
 * traço não.
 */
function RotuloDaParede({
  texto,
  de,
  ate,
  zoom,
  cor,
}: {
  texto: string;
  de: Pixel;
  ate: Pixel;
  zoom: number;
  cor: string;
}) {
  const comprimento = distance(de, ate);
  const tamanho = 10 / zoom;
  const cabe = (candidato: string): boolean => candidato.length * tamanho * 0.55 <= comprimento;

  // "Sala · Lado 1" não cabe numa parede de 1 m; "Lado 1" talvez caiba. O ambiente já está
  // escrito no meio do polígono, então o que se perde aqui está ali do lado.
  const escolhido = cabe(texto) ? texto : (texto.split('·').pop()?.trim() ?? texto);
  if (!cabe(escolhido)) return null;

  // De cabeça para baixo ninguém lê: a parede da esquerda e a da direita são a mesma reta.
  let angulo = (Math.atan2(ate.y - de.y, ate.x - de.x) * 180) / Math.PI;
  if (angulo > 90) angulo -= 180;
  if (angulo < -90) angulo += 180;

  return (
    <text
      transform={`translate(${String((de.x + ate.x) / 2)} ${String((de.y + ate.y) / 2)}) rotate(${String(angulo)})`}
      dy={-tamanho * 0.6}
      textAnchor="middle"
      fontSize={tamanho}
      fontWeight={600}
      fill={cor}
      stroke="#ffffff"
      strokeWidth={tamanho * 0.28}
      paintOrder="stroke"
      className="pointer-events-none select-none"
    >
      {escolhido}
    </text>
  );
}

/** Um texto legível sobre a prancha — o halo branco é o que o separa da linha do desenho. */
function Rotulo({
  texto,
  em,
  zoom,
  cor,
  tamanho = 10,
  peso = 500,
}: {
  texto: string;
  em: Pixel;
  zoom: number;
  cor: string;
  tamanho?: number;
  peso?: number;
}) {
  const fonte = tamanho / zoom;
  return (
    <text
      x={em.x}
      y={em.y}
      textAnchor="middle"
      fontSize={fonte}
      fontWeight={peso}
      fill={cor}
      stroke="#ffffff"
      strokeWidth={fonte * 0.28}
      paintOrder="stroke"
      className="pointer-events-none select-none"
    >
      {texto}
    </text>
  );
}

/**
 * Os vãos, em cima da parede em que estão.
 *
 * O posto pela prancha sai cheio: ele foi clicado ali. O cadastrado no cartão sai
 * tracejado, porque a **posição é deduzida** — a largura é de verdade, o lugar é o melhor
 * palpite, e dizer isso no traço é mais honesto do que desenhar uma porta cheia onde
 * ninguém garantiu que ela está.
 *
 * Com a prancha parada, clicar no vão o seleciona para excluir. O clique para aqui: se
 * subisse, a prancha o leria como clique no vazio e desfaria a seleção.
 */
function VaosDesenhados({
  vaos,
  escala,
  zoom,
  selecionado,
  onSelecionar,
}: {
  vaos: readonly PlacedOpening[];
  escala: number;
  zoom: number;
  selecionado: OpeningId | undefined;
  onSelecionar: ((openingId: OpeningId) => void) | undefined;
}) {
  return (
    <g className={onSelecionar === undefined ? 'pointer-events-none' : 'cursor-pointer'}>
      {vaos.map((vao, indice) => {
        const de = metresToPixel(vao.segment.from, escala);
        const ate = metresToPixel(vao.segment.to, escala);
        const escolhido = vao.openingId === selecionado;

        return (
          <line
            key={`${String(vao.openingId)}-${String(indice)}`}
            data-vao={vao.openingId}
            x1={de.x}
            y1={de.y}
            x2={ate.x}
            y2={ate.y}
            stroke={CORES[vao.kind]}
            strokeWidth={
              Math.max(4 / zoom, DEFAULT_WALL_THICKNESS / escala) * (escolhido ? 1.8 : 1)
            }
            {...(vao.certain ? {} : { strokeDasharray: `${5 / zoom} ${3 / zoom}` })}
            strokeLinecap="butt"
            onClick={(evento) => {
              if (onSelecionar === undefined) return;
              evento.stopPropagation();
              onSelecionar(vao.openingId);
            }}
          >
            <title>{OPENING_KIND_LABEL[vao.kind]}</title>
          </line>
        );
      })}
    </g>
  );
}

/**
 * Pilar e viga, cada um com a cor dele.
 *
 * **A viga é sempre tracejada**, como em prancha de verdade: ela está acima do plano de
 * corte, e o traço cheio diria que ela é o que se vê ao passar pelo ambiente. Tem o
 * efeito prático de deixar a parede aparecer por baixo — viga e parede têm a mesma
 * largura, e a faixa cheia apagava justamente a parede em que a viga se apoia.
 *
 * O preenchimento fraco é o outro recado, o mesmo dos vãos: a peça está naquela parede, e
 * em que ponto dela o cadastro não diz. Forte é lugar sabido — o pilar de canto e a viga
 * que corre a parede inteira.
 */
function EstruturaDesenhada({
  pecas,
  escala,
  zoom,
  selecionado,
  arrastando,
  onSelecionar,
}: {
  pecas: readonly PlacedElement[];
  escala: number;
  zoom: number;
  selecionado: StructuralElementId | undefined;
  /** O elemento com contorno na mão: quanto ele andou, em pixels da prancha. */
  arrastando:
    { readonly id: StructuralElementId; readonly dx: number; readonly dy: number } | undefined;
  /** Ausente enquanto se traça: aí o clique é do traço, e a peça deixa passar. */
  onSelecionar: ((elementId: StructuralElementId) => void) | undefined;
}) {
  return (
    <g className={onSelecionar === undefined ? 'pointer-events-none' : 'cursor-pointer'}>
      {pecas.map((peca, indice) => {
        const naMao = arrastando !== undefined && arrastando.id === peca.elementId;
        const dx = naMao ? arrastando.dx : 0;
        const dy = naMao ? arrastando.dy : 0;
        const vertices = peca.corners.map((canto) => {
          const pixel = metresToPixel(canto, escala);
          return { x: pixel.x + dx, y: pixel.y + dy };
        });
        const centro = centroDoContorno(peca.corners);
        const ancora = metresToPixel(centro, escala);
        const fonte = 11 / zoom;

        return (
          <g key={`${String(peca.elementId)}-${String(indice)}`}>
            <polygon
              data-peca={peca.elementId}
              // O elemento com contorno é o que anda: o arrasto procura por esta marca.
              data-peca-contorno={peca.comContorno === true ? '' : undefined}
              onClick={(evento) => {
                if (onSelecionar === undefined) return;
                evento.stopPropagation();
                onSelecionar(peca.elementId);
              }}
              points={pontosDe(vertices)}
              fill={CORES[peca.kind]}
              fillOpacity={peca.certain ? 0.4 : 0.15}
              stroke={CORES[peca.kind]}
              strokeWidth={(peca.elementId === selecionado ? 4 : 1.5) / zoom}
              {...(peca.kind === 'VIGA' || !peca.certain
                ? { strokeDasharray: `${5 / zoom} ${3 / zoom}` }
                : {})}
            >
              <title>
                {STRUCTURAL_ELEMENT_KIND_LABEL[peca.kind]}
                {peca.certain ? '' : ' — posição aproximada'}
              </title>
            </polygon>
            {peca.comContorno === true && (
              <text
                data-rotulo=""
                x={ancora.x + dx}
                y={ancora.y + dy}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={fonte}
                fontWeight={600}
                fill={CORES[peca.kind]}
                stroke="#ffffff"
                strokeWidth={fonte * 0.22}
                paintOrder="stroke"
                className="pointer-events-none select-none"
              >
                {`${peca.name ?? STRUCTURAL_ELEMENT_KIND_LABEL[peca.kind]} · ${metros(
                  areaDoContorno(peca.corners),
                )} m²`}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}

/**
 * Os shafts, com o símbolo que a planta de arquitetura usa para o vazio: o retângulo
 * cortado por um **X**.
 *
 * O X é o que o separa do pilar à primeira vista — os dois são retângulos pequenos vistos
 * de cima, e o pilar é concreto onde o shaft é ar. Preenchimento fraco, contorno cheio: o
 * lugar do buraco é sabido, porque foi traçado (D21).
 */
function ShaftsDesenhados({
  shafts,
  escala,
  zoom,
  selecionado,
  onSelecionar,
}: {
  shafts: readonly Shaft[];
  escala: number;
  zoom: number;
  selecionado: ShaftId | undefined;
  /** Ausente enquanto se traça: aí o clique é do traço, e o shaft deixa passar. */
  onSelecionar: ((shaftId: ShaftId) => void) | undefined;
}) {
  return (
    <g className={onSelecionar === undefined ? 'pointer-events-none' : 'cursor-pointer'}>
      {shafts.map((shaft) => {
        const a = metresToPixel(shaft.from, escala);
        const b = metresToPixel(shaft.to, escala);
        const x = Math.min(a.x, b.x);
        const y = Math.min(a.y, b.y);
        const largura = Math.abs(b.x - a.x);
        const altura = Math.abs(b.y - a.y);
        const traco = (shaft.id === selecionado ? 4 : 1.5) / zoom;

        return (
          <g
            key={shaft.id}
            data-shaft={shaft.id}
            onClick={(evento) => {
              if (onSelecionar === undefined) return;
              evento.stopPropagation();
              onSelecionar(shaft.id);
            }}
          >
            <title>
              {SHAFT_LABEL} — {cm(shaftWidth(shaft))} × {cm(shaftLength(shaft))} cm
            </title>
            <rect
              x={x}
              y={y}
              width={largura}
              height={altura}
              fill={CORES.SHAFT}
              fillOpacity={0.15}
              stroke={CORES.SHAFT}
              strokeWidth={traco}
            />
            <line
              x1={x}
              y1={y}
              x2={x + largura}
              y2={y + altura}
              stroke={CORES.SHAFT}
              strokeWidth={traco}
            />
            <line
              x1={x + largura}
              y1={y}
              x2={x}
              y2={y + altura}
              stroke={CORES.SHAFT}
              strokeWidth={traco}
            />
          </g>
        );
      })}
    </g>
  );
}

/** A legenda: sem ela, cor vira enfeite. */
function Legenda({
  comVizinhas,
  comEstaca,
  comRadier,
  comLaje,
  comEscada,
  comPecasDaPlanilha = false,
}: {
  comVizinhas: boolean;
  comEstaca: boolean;
  comRadier: boolean;
  comLaje: boolean;
  comEscada: boolean;
  /** As peças da planilha na planta (Fase 28, etapa 3): a cor é a execução delas. */
  comPecasDaPlanilha?: boolean;
}) {
  const itens: readonly { readonly cor: string; readonly texto: string }[] = [
    ...(comPecasDaPlanilha ? LEGENDA_DA_EXECUCAO : []),
    ...(comEstaca
      ? [
          { cor: COR_DA_ESTACA, texto: 'Estaca' },
          { cor: COR_DA_ESTACA_MARCADA, texto: 'Estaca marcada' },
          { cor: COR_DO_BLOCO, texto: 'Bloco' },
          { cor: COR_DO_BALDRAME, texto: 'Baldrame' },
          { cor: COR_DO_MURO, texto: 'Muro de contenção' },
          { cor: COR_DO_PILAR_DE_CONTENCAO, texto: 'Pilar de contenção' },
        ]
      : []),
    ...(comRadier ? [{ cor: COR_DO_RADIER, texto: 'Radier' }] : []),
    ...(comLaje ? [{ cor: COR_DA_LAJE, texto: 'Laje' }] : []),
    ...(comEscada ? [{ cor: COR_DA_ESCADA, texto: 'Escada' }] : []),
    { cor: CORES.ambiente, texto: 'Ambiente' },
    ...(comVizinhas ? [{ cor: CORES.vizinha, texto: 'Outra tipologia' }] : []),
    { cor: CORES.parede, texto: 'Parede' },
    { cor: CORES.guardaCorpo, texto: 'Guarda-corpo' },
    { cor: CORES.PORTA, texto: 'Porta' },
    { cor: CORES.JANELA, texto: 'Janela' },
    { cor: CORES.PASSAGEM, texto: 'Passagem' },
    { cor: CORES.PILAR, texto: 'Pilar' },
    { cor: CORES.VIGA, texto: 'Viga' },
    { cor: CORES.SHAFT, texto: 'Shaft' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-xs text-(--color-content-muted)">
      {itens.map((item) => (
        <span key={item.texto} className="flex items-center gap-1">
          <span aria-hidden className="size-2.5 rounded-xs" style={{ backgroundColor: item.cor }} />
          {item.texto}
        </span>
      ))}
      <span className="italic">tracejado = posição aproximada</span>
    </div>
  );
}

// ── Painel: a peça de estrutura em traçado ──────────────────────────────────────

/** O nome curto da parede, para a lista dizer onde a peça caiu. */
function wallShortLabel(draft: TypologyDraft, wallId: WallId): string {
  return wallShortName(draft.labels[wallId]);
}

/**
 * Pilar e viga traçados: dois cliques em cantos opostos, e o que sobe fora do papel.
 *
 * O painel mostra **em quais paredes a peça caiu** antes de ela existir, e esse é o ponto
 * da fatia: é a resposta que a digitação não dá sozinha — quem cadastra no cartão da parede
 * sabe daquela, e a viga que atravessa três precisa ser apontada três vezes.
 *
 * A medida que falta é pedida, e não palpitada. A planta é vista de cima: ela mostra a
 * seção do pilar e a corrida da viga, e não mostra a altura de nenhum dos dois — e a altura
 * da viga é justamente o que come a alvenaria (RN-183).
 */
function EmEstrutura({
  kind,
  cantos,
  peca,
  pecaLivre,
  impedimento,
  avisoDaMedida,
  medida,
  largura,
  nomeDaParede,
  onKind,
  onMedida,
  onLargura,
  onForma,
  onRefazer,
  onCadastrar,
  onCancelar,
}: {
  kind: StructuralElementKind;
  /** Quantos cantos do retângulo já foram clicados — zero, um ou dois. */
  cantos: number;
  /** O que o traço diz sobre a peça — ausente enquanto o retângulo ou o polígono não fechou. */
  peca: TracedStructure | undefined;
  /** O polígono em traço e a forma escolhida. */
  pecaLivre: PecaLivreNaPrancha;
  /** O que impede o cadastro — o contorno sem área, a viga mais larga que o contorno. */
  impedimento: string | undefined;
  /** O pedido de conferir a altura que passa do que a obra tem. */
  avisoDaMedida: string | undefined;
  medida: string;
  /** A largura da viga em polígono, digitada. */
  largura: string;
  nomeDaParede: (wallId: WallId) => string;
  onKind: (kind: StructuralElementKind) => void;
  onMedida: (medida: string) => void;
  onLargura: (largura: string) => void;
  onForma: (formato: FormatoDaPeca) => void;
  onRefazer: () => void;
  onCadastrar: () => void;
  onCancelar: () => void;
}) {
  const ehPilar = kind === 'PILAR';
  const emPoligono = pecaLivre.formato === 'POLIGONO';
  const emParede = peca?.segments ?? [];
  const pronto = peca !== undefined;

  return (
    <section
      aria-label="Pilar e viga"
      className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3"
    >
      <h3 className="text-sm font-semibold">Pilar e viga</h3>

      <FormaDaPeca peca={pecaLivre} cantos={cantos} onEscolher={onForma} onRefazer={onRefazer} />

      <div className="flex flex-wrap gap-2">
        {(['PILAR', 'VIGA'] as const).map((candidato) => (
          <Button
            key={candidato}
            size="sm"
            variant={candidato === kind ? 'primary' : 'ghost'}
            onClick={() => onKind(candidato)}
          >
            {STRUCTURAL_ELEMENT_KIND_LABEL[candidato]}
          </Button>
        ))}
      </div>

      {!pronto ? (
        <span className="text-xs text-(--color-content-muted)">
          {emPoligono
            ? pecaLivre.vertices === 0
              ? 'Nenhum ponto marcado na planta.'
              : `${String(pecaLivre.vertices)} ${
                  pecaLivre.vertices === 1 ? 'ponto' : 'pontos'
                } no contorno`
            : `${String(cantos)} de 2 cantos`}
        </span>
      ) : (
        <dl className="flex flex-col gap-1 text-xs">
          <div className="flex justify-between gap-3">
            <dt className="text-(--color-content-muted)">
              {ehPilar ? 'Seção em planta' : 'Largura × corrida'}
            </dt>
            <dd className="tabular-nums font-medium">
              {ehPilar
                ? `${cm(peca?.sectionWidth)} × ${cm(peca?.sectionDepth)} cm`
                : `${cm(peca?.sectionWidth)} cm × ${(peca?.run ?? 0).toFixed(2)} m`}
            </dd>
          </div>
        </dl>
      )}

      {/*
        A medida que a planta não vê. Ela vem antes do botão de propósito: pedi-la depois
        seria aceitar o traço e só então descobrir que falta o número que decide a conta.
      */}
      {emPoligono && !ehPilar && (
        <Field label="Largura da viga (m)" htmlFor="largura-da-viga">
          <Input
            id="largura-da-viga"
            inputMode="decimal"
            value={largura}
            onChange={(evento) => onLargura(evento.target.value)}
            className="w-28"
          />
        </Field>
      )}
      <Field
        label={ehPilar ? 'Altura do pilar (m)' : 'Altura da seção da viga (m)'}
        htmlFor="medida-da-peca"
      >
        <Input
          id="medida-da-peca"
          inputMode="decimal"
          value={medida}
          onChange={(evento) => onMedida(evento.target.value)}
          className="w-28"
        />
      </Field>

      {pronto && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-(--color-content-muted)">
            {emParede.length === 0
              ? 'Nenhuma parede embaixo do traço'
              : emParede.length === 1
                ? 'Em 1 parede'
                : `Em ${String(emParede.length)} paredes`}
          </span>
          {emParede.map((trecho) => (
            <span key={trecho.wallId} className="text-xs text-(--color-content-muted)">
              {nomeDaParede(trecho.wallId)} — {orientationLabel(kind, trecho.orientation)}
              {trecho.length === undefined ? '' : ` · trecho de ${trecho.length.toFixed(2)} m`}
            </span>
          ))}
          {emParede.length === 0 && (
            <span className="text-xs text-(--color-content-muted)">
              A peça fica solta na planta, pelo contorno — sem parede, não desconta alvenaria.
            </span>
          )}
        </div>
      )}
      {impedimento !== undefined && (
        <span className="text-xs text-(--color-state-warn)">{impedimento}</span>
      )}
      {avisoDaMedida !== undefined && (
        <span className="text-xs text-(--color-state-warn)">{avisoDaMedida}</span>
      )}

      <div className="flex gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={!pronto || impedimento !== undefined}
          onClick={onCadastrar}
        >
          Cadastrar {STRUCTURAL_ELEMENT_KIND_LABEL[kind].toLocaleLowerCase('pt-BR')}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancelar}>
          Sair
        </Button>
      </div>
    </section>
  );
}

/** Centímetros inteiros — a seção é como a obra fala: "20 × 40". */
function cm(metros: number | undefined): string {
  return String(Math.round((metros ?? 0) * 100));
}

/**
 * O vão a pôr: o tipo, a medida e onde clicar.
 *
 * A medida vem antes do clique porque é a medida do projeto, e não a do traço: a prancha
 * mostra a porta como um arco, e ninguém mede 0,80 por ele. O que o clique diz é **onde**.
 */
function EmVao({
  kind,
  largura,
  altura,
  onKind,
  onLargura,
  onAltura,
  onSair,
}: {
  kind: OpeningKind;
  largura: string;
  altura: string;
  onKind: (kind: OpeningKind) => void;
  onLargura: (valor: string) => void;
  onAltura: (valor: string) => void;
  onSair: () => void;
}) {
  return (
    <section className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3">
      <h3 className="text-sm font-semibold">Vão</h3>
      <p className="text-xs text-(--color-content-muted)">
        Escolha o tipo e a medida, e clique na parede onde fica o vão — ele entra centrado no
        clique. Siga clicando para pôr outros iguais.
      </p>
      <div className="flex flex-wrap gap-1">
        {(['PORTA', 'JANELA', 'PASSAGEM'] as const).map((candidato) => (
          <Button
            key={candidato}
            size="sm"
            variant={candidato === kind ? 'primary' : 'ghost'}
            aria-pressed={candidato === kind}
            onClick={() => onKind(candidato)}
          >
            {OPENING_KIND_LABEL[candidato]}
          </Button>
        ))}
      </div>
      <div className="flex items-end gap-2">
        <Field label="Largura (m)" htmlFor="vao-largura">
          <Input
            id="vao-largura"
            inputMode="decimal"
            value={largura}
            onChange={(evento) => onLargura(evento.target.value)}
            className="w-24"
          />
        </Field>
        <Field label="Altura (m)" htmlFor="vao-altura">
          <Input
            id="vao-altura"
            inputMode="decimal"
            value={altura}
            onChange={(evento) => onAltura(evento.target.value)}
            className="w-24"
          />
        </Field>
      </div>
      <Button variant="ghost" size="sm" onClick={onSair}>
        Sair
      </Button>
    </section>
  );
}

/**
 * O shaft a traçar: dois cantos, e o ambiente de que ele vai descontar.
 *
 * O painel diz as três medidas do buraco — área, requadro e o ambiente — antes do cadastro,
 * pela mesma razão do pilar: é a resposta que a digitação não dá. O ambiente que não aparece
 * é o aviso de que o traço caiu fora de qualquer contorno, e aí não há piso a descontar.
 */
function EmShaft({
  cantos,
  shaft,
  iguais,
  onIguais,
  onCadastrar,
  onCancelar,
}: {
  /** Quantos cantos já foram clicados — zero, um ou dois. */
  cantos: number;
  shaft: { readonly from: Point; readonly to: Point; readonly room: Room | undefined } | undefined;
  iguais: string;
  onIguais: (valor: string) => void;
  onCadastrar: () => void;
  onCancelar: () => void;
}) {
  const umSo = shaft === undefined ? undefined : { ...shaft, count: 1 };

  return (
    <section
      className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3"
      aria-label="Shaft a traçar"
    >
      <h3 className="text-sm font-semibold">{SHAFT_LABEL}</h3>
      <p className="text-xs text-(--color-content-muted)">
        Arraste de um canto ao outro do buraco — ou clique nos dois cantos. Ele desconta do piso e
        do teto do ambiente em que cair.
      </p>

      {umSo === undefined ? (
        <span className="text-xs text-(--color-content-muted)">{cantos} de 2 cantos</span>
      ) : (
        <dl className="flex flex-col gap-1 text-xs">
          <div className="flex justify-between gap-3">
            <dt className="text-(--color-content-muted)">Medida</dt>
            <dd className="tabular-nums font-medium">
              {cm(shaftWidth(umSo))} × {cm(shaftLength(umSo))} cm
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-(--color-content-muted)">Área · requadro</dt>
            <dd className="tabular-nums font-medium">
              {shaftArea(umSo).toFixed(2)} m² · {shaftContour(umSo).toFixed(2)} m
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-(--color-content-muted)">Ambiente</dt>
            <dd className="font-medium">{umSo.room?.name ?? '—'}</dd>
          </div>
        </dl>
      )}
      {umSo !== undefined && umSo.room === undefined && (
        <span className="text-xs text-(--color-state-warn)">
          O shaft precisa cair dentro de um ambiente traçado — é do piso dele que o buraco sai.
        </span>
      )}

      <Field label="Quantos iguais" htmlFor="shafts-iguais">
        <Input
          id="shafts-iguais"
          inputMode="numeric"
          value={iguais}
          onChange={(evento) => onIguais(evento.target.value)}
          className="w-20"
        />
      </Field>

      <div className="flex gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={umSo === undefined || umSo.room === undefined}
          onClick={onCadastrar}
        >
          Cadastrar shaft
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancelar}>
          Sair
        </Button>
      </div>
    </section>
  );
}

/**
 * Os vãos e as peças da tipologia, cada um com o botão de excluir.
 *
 * A lista é o caminho que não depende de mira: um pilar de 20 cm com o zoom afastado é um
 * ponto, e acertá-lo para selecionar é loteria. Clicar numa linha a destaca no desenho; a
 * lixeira exclui direto — vão e peça se põem de novo com um clique, e a confirmação fica
 * para o que leva coisa junto, que é o ambiente.
 *
 * O pilar e a viga selecionados abrem, na própria linha, o tipo e os fatores deles (Fase 42,
 * RN-561 a RN-563) — `detalheDaPeca`. A linha diz o tipo ao lado da seção.
 */
function VaosEPecas({
  draft,
  tipos,
  selecionada,
  onSelecionar,
  onExcluir,
  detalheDaPeca,
}: {
  draft: TypologyDraft;
  /** Os tipos de pilar e de viga da obra, para a linha dizer o tipo de cada peça. */
  tipos?: readonly StructuralElementType[] | undefined;
  selecionada: PecaSelecionada | undefined;
  onSelecionar: (peca: PecaSelecionada) => void;
  onExcluir: (peca: PecaSelecionada) => void;
  /** O que a linha do pilar ou da viga selecionado mostra embaixo dela. */
  detalheDaPeca?: (elemento: StructuralElement) => React.ReactNode;
}) {
  const pecas = draft.elements.filter((element) => element.deletedAt === undefined);
  const shafts = draft.shafts ?? [];
  if (draft.openings.length === 0 && pecas.length === 0 && shafts.length === 0) return null;
  const nomeDoAmbiente = new Map(draft.rooms.map((room) => [String(room.id), room.name]));
  const nomeDoTipo = new Map(
    (tipos ?? []).map((tipo) => [String(tipo.id), { kind: tipo.kind, name: tipo.name }]),
  );
  const doTipo = (element: StructuralElement): string => {
    const tipo =
      element.elementTypeId === undefined ? undefined : nomeDoTipo.get(element.elementTypeId);
    return tipo === undefined || tipo.kind !== element.kind ? '' : ` · ${tipo.name}`;
  };

  const itens: readonly { readonly peca: PecaSelecionada; readonly rotulo: string }[] = [
    ...draft.openings.map((opening) => ({
      peca: { tipo: 'VAO', id: opening.id } as const,
      rotulo: `${opening.count > 1 ? `${String(opening.count)} × ` : ''}${
        OPENING_KIND_LABEL[opening.kind]
      } ${opening.width.toFixed(2)} × ${opening.height.toFixed(2)} m · ${wallShortLabel(
        draft,
        opening.wallId,
      )}`,
    })),
    ...pecas.map((element) => ({
      peca: { tipo: 'PECA', id: element.id } as const,
      rotulo: `${STRUCTURAL_ELEMENT_KIND_LABEL[element.kind]} ${String(
        Math.round(element.sectionWidth * 100),
      )} × ${String(Math.round(element.sectionDepth * 100))} cm${doTipo(element)}`,
    })),
    ...shafts.map((shaft) => ({
      peca: { tipo: 'SHAFT', id: shaft.id } as const,
      rotulo: `${shaft.count > 1 ? `${String(shaft.count)} × ` : ''}Shaft ${cm(
        shaftWidth(shaft),
      )} × ${cm(shaftLength(shaft))} cm · ${nomeDoAmbiente.get(String(shaft.roomId)) ?? 'sem ambiente'}`,
    })),
  ];

  return (
    <section
      className="flex flex-col gap-2"
      aria-label={`Vãos e peças de ${nomeDaTipologia(draft)}`}
    >
      <h3 className="text-sm font-semibold">Vãos e peças de {nomeDaTipologia(draft)}</h3>
      <ul className="flex flex-col gap-1">
        {itens.map(({ peca, rotulo }) => {
          const escolhida = selecionada?.tipo === peca.tipo && selecionada.id === peca.id;
          const elemento =
            escolhida && peca.tipo === 'PECA'
              ? pecas.find((element) => element.id === peca.id)
              : undefined;
          return (
            <li
              key={`${peca.tipo}-${String(peca.id)}`}
              className={
                escolhida
                  ? 'flex flex-col rounded-md border border-(--color-brand) px-2'
                  : 'flex flex-col rounded-md border border-(--color-border-subtle) px-2'
              }
            >
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate py-1.5 text-left text-xs"
                  aria-pressed={escolhida}
                  onClick={() => onSelecionar(peca)}
                >
                  {rotulo}
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Excluir ${rotulo}`}
                  onClick={() => onExcluir(peca)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
              {elemento !== undefined && detalheDaPeca?.(elemento)}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
